import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import BracketDiagram, { TournamentFlowStepper } from "@/components/BracketDiagram";
import QRCode from "react-qr-code";
import { MatchCard } from "@/components/admin/MatchCard";
import { QRDialog } from "@/components/admin/QRDialog";
import { InviteDialog } from "@/components/admin/InviteDialog";
import { ScoreDialog } from "@/components/admin/ScoreDialog";
import { RosterDialog } from "@/components/admin/RosterDialog";
import { SetupGuideDialog } from "@/components/admin/SetupGuideDialog";
import { GrantCreditsDialog } from "@/components/admin/GrantCreditsDialog";
import {
  Target, Plus, Trash2, Shuffle, Play, Trophy, ArrowRight,
  Download, LogOut, CalendarIcon, User, Eye, CheckCircle,
  QrCode, History, Shield, UserCheck, UserX, BarChart3, Users, RotateCcw, Trash2 as TrashIcon, AlertTriangle, UserPlus, BookOpen, MoreVertical, ArrowRightLeft, X, Swords, KeyRound, Copy, Printer, Coins,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [token, setToken] = useState("");

  const [event, setEvent] = useState(null);
  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState(new Date());
  const [numBoards, setNumBoards] = useState(4);
  const [eventType, setEventType] = useState("social");
  const [gameFormat, setGameFormat] = useState("501");
  const [playMode, setPlayMode] = useState("doubles");
  const [paceMode, setPaceMode] = useState("round_by_round");
  const [teamSize, setTeamSize] = useState(4);
  const [superAdminVenueId, setSuperAdminVenueId] = useState("");
  const [teamBuilderPlayers, setTeamBuilderPlayers] = useState([]);
  const [teamBuilderName, setTeamBuilderName] = useState("");
  const [teamBuilderInput, setTeamBuilderInput] = useState("");
  const [teamBuilderMobile, setTeamBuilderMobile] = useState("");
  const [teamBuilderSuggestions, setTeamBuilderSuggestions] = useState([]);
  const [players, setPlayers] = useState([]);
  const [newPlayerName, setNewPlayerName] = useState("");
  const [newPlayerMobile, setNewPlayerMobile] = useState("");
  const [teams, setTeams] = useState([]);
  const [reservePlayer, setReservePlayer] = useState(null);
  const [currentRound, setCurrentRound] = useState(null);
  const [loading, setLoading] = useState({});
  const [role, setRole] = useState("");
  const [showQR, setShowQR] = useState(false);
  const [showGrantCredits, setShowGrantCredits] = useState(false);
  const [pendingAdmins, setPendingAdmins] = useState([]);
  const [allAdmins, setAllAdmins] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [careerStats, setCareerStats] = useState([]);
  const [historyStats, setHistoryStats] = useState(null);
  const [roster, setRoster] = useState([]);
  const [showRoster, setShowRoster] = useState(false);
  const [rosterSearch, setRosterSearch] = useState("");
  const [selectedRoster, setSelectedRoster] = useState(new Set());
  const [suggestions, setSuggestions] = useState([]);
  const [tournamentState, setTournamentState] = useState(null);
  const [knockoutState, setKnockoutState] = useState(null);
  const [koConfirmMatch, setKoConfirmMatch] = useState(null);
  const [koConfirmWinner, setKoConfirmWinner] = useState(null);
  const [adminRoster, setAdminRoster] = useState([]);
  const [adminRosterSearch, setAdminRosterSearch] = useState("");
  const [scoreDialog, setScoreDialog] = useState({ open: false, match: null });
  const [scoreForm, setScoreForm] = useState({ hsPlayer: "", hsValue: "", lsPlayer: "", lsValue: "", hcPlayer: "", hcValue: "" });
  const [boardStatus, setBoardStatus] = useState(null);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [nextUpMatches, setNextUpMatches] = useState([]);
  const [availableBoards, setAvailableBoards] = useState(0);
  const [venueName, setVenueName] = useState("");
  const [venueId, setVenueId] = useState("");
  const [showGuide, setShowGuide] = useState(false);
  const [subDialog, setSubDialog] = useState({ open: false, team: null, oldPlayer: "" });
  const [subNewPlayer, setSubNewPlayer] = useState("");
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [addTeamForm, setAddTeamForm] = useState({ player1: "", player1_mobile: "", player2: "", player2_mobile: "", team_name: "", teamPlayers: [], teamMobiles: [] });
  const [allVenues, setAllVenues] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [activeTab, setActiveTab] = useState("setup");
  const [resetPwResult, setResetPwResult] = useState(null);
  const [venueBoards, setVenueBoards] = useState([]);
  const [selectedBoardIds, setSelectedBoardIds] = useState(new Set());

  const authConfig = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const applyUpcoming = (data) => {
    setNextUpMatches(data?.next_up || []);
    setUpcomingMatches(data?.waiting || []);
    setAvailableBoards(data?.available_boards || 0);
  };

  useEffect(() => {
    const t = localStorage.getItem("darts_token");
    if (!t) { navigate("/admin/login"); return; }
    setToken(t);
    setRole(localStorage.getItem("darts_role") || "admin");
    setVenueId(localStorage.getItem("darts_venue_id") || "");
    setVenueName(localStorage.getItem("darts_venue_name") || "");

    const interceptor = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401 && err.config?.headers?.Authorization) {
          localStorage.removeItem("darts_token");
          localStorage.removeItem("darts_username");
          navigate("/admin/login");
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [navigate]);

  useEffect(() => {
    if (!token) return;
    loadActiveEvent();
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (r.data.venue_name) setVenueName(r.data.venue_name);
        if (r.data.venue_id) { setVenueId(r.data.venue_id); loadVenueBoards(r.data.venue_id); }
        if (r.data.role === "super_admin") loadAllVenues();
      }).catch(() => {});
  }, [token]);

  const loadActiveEvent = async () => {
    try {
      const vid = localStorage.getItem("darts_venue_id") || "";
      const res = await axios.get(`${API}/events/active${vid ? `?venue=${vid}` : ""}`);
      if (res.data) {
        setEvent(res.data);
        loadEventData(res.data.id);
        if (res.data.venue_id) loadVenueBoards(res.data.venue_id);
        // Pre-select event boards if set
        if (res.data.board_ids) setSelectedBoardIds(new Set(res.data.board_ids));
      }
    } catch (e) {
      console.error("Failed to load active event", e);
    }
  };

  const loadEventData = async (eventId) => {
    try {
      const [pRes, tRes, mRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}/players`),
        axios.get(`${API}/events/${eventId}/teams`),
        axios.get(`${API}/events/${eventId}/matches`),
      ]);
      setPlayers(pRes.data);
      setTeams(tRes.data);
      if (tRes.data.length > 0 && mRes.data.length > 0) {
        const rRes = await axios.get(`${API}/events/${eventId}/matches/current-round`);
        setCurrentRound(rRes.data);
      }
      // Load board status for continuous mode
      try {
        const bsRes = await axios.get(`${API}/events/${eventId}/matches/board-status`);
        setBoardStatus(bsRes.data);
      } catch (e) { /* no board status */ }
      // Load upcoming/queued matches
      try {
        const upRes = await axios.get(`${API}/events/${eventId}/matches/upcoming`);
        setNextUpMatches(upRes.data?.next_up || []);
        setUpcomingMatches(upRes.data?.waiting || []);
        setAvailableBoards(upRes.data?.available_boards || 0);
      } catch (e) { setUpcomingMatches([]); setNextUpMatches([]); }
      // Load tournament state if tournament event
      try {
        const tsRes = await axios.get(`${API}/events/${eventId}/tournament/state`);
        setTournamentState(tsRes.data);
      } catch (e) { /* not a tournament or no state yet */ }
      // Load knockout state if knockout event
      try {
        const koRes = await axios.get(`${API}/events/${eventId}/knockout/state`);
        setKnockoutState(koRes.data);
      } catch (e) { /* not a knockout or no state yet */ }
    } catch (e) { console.error(e); }
  };

  const createEvent = async () => {
    setLoading((p) => ({ ...p, createEvent: true }));
    try {
      const payload = {
        name: eventName, date: format(eventDate, "yyyy-MM-dd"),
        num_boards: selectedBoardIds.size || 4, event_type: eventType,
        board_ids: Array.from(selectedBoardIds),
        game_format: gameFormat, play_mode: playMode,
        pace_mode: paceMode,
        ...(playMode === "teams" ? { team_size: teamSize } : {}),
        ...(role === "super_admin" && superAdminVenueId ? { venue_id: superAdminVenueId } : {}),
      };
      const res = await axios.post(`${API}/events`, payload, authConfig());
      setEvent(res.data);
      setPlayers([]); setTeams([]); setCurrentRound(null);
      setTeamBuilderPlayers([]); setTeamBuilderName(""); setTeamBuilderInput("");
      setEventName("");
      toast.success("Event created!");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create event"); }
    finally { setLoading((p) => ({ ...p, createEvent: false })); }
  };

  const addPlayer = async () => {
    if (!newPlayerName.trim() || !newPlayerMobile.trim() || !event) return;
    setLoading((p) => ({ ...p, addPlayer: true }));
    setSuggestions([]);
    try {
      const res = await axios.post(`${API}/events/${event.id}/players`, {
        players: [{ name: newPlayerName.trim(), mobile: newPlayerMobile.trim() }],
      }, authConfig());
      setPlayers((prev) => [...prev, ...res.data]);
      setNewPlayerName(""); setNewPlayerMobile("");
      toast.success("Player added! (1 credit used)");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add player"); }
    finally { setLoading((p) => ({ ...p, addPlayer: false })); }
  };

  const removePlayer = async (playerId) => {
    try {
      await axios.delete(`${API}/events/${event.id}/players/${playerId}`, authConfig());
      setPlayers((prev) => prev.filter((p) => p.id !== playerId));
      toast.success("Player removed");
    } catch (e) { toast.error("Failed"); }
  };

  const generateTeams = async () => {
    if (!event) return;
    setLoading((p) => ({ ...p, generateTeams: true }));
    try {
      const res = await axios.post(`${API}/events/${event.id}/teams/generate`, {}, authConfig());
      setTeams(res.data.teams);
      setReservePlayer(res.data.reserve_player || null);
      setCurrentRound(null);
      setEvent((prev) => ({ ...prev, status: "teams_generated" }));
      toast.success("Teams generated!");
    } catch (e) { toast.error(e.response?.data?.detail || "Need at least 4 players"); }
    finally { setLoading((p) => ({ ...p, generateTeams: false })); }
  };

  const generateSchedule = async () => {
    if (!event) return;
    setLoading((p) => ({ ...p, generateSchedule: true }));
    try {
      const res = await axios.post(`${API}/events/${event.id}/matches/generate`, {}, authConfig());
      const eventRefresh = await axios.get(`${API}/events/${event.id}`);
      setEvent(eventRefresh.data);

      if (res.data.pace_mode === "continuous" || eventRefresh.data.pace_mode === "continuous") {
        const [bsRes, upRes] = await Promise.all([
          axios.get(`${API}/events/${event.id}/matches/board-status`),
          axios.get(`${API}/events/${event.id}/matches/upcoming`),
        ]);
        setBoardStatus(bsRes.data);
        applyUpcoming(upRes.data);
        toast.success(`Schedule created! ${res.data.total_matches} matches across ${res.data.effective_boards} board${res.data.effective_boards !== 1 ? "s" : ""} - Continuous Play`);
      } else {
        const [roundRes, upRes] = await Promise.all([
          axios.get(`${API}/events/${event.id}/matches/current-round`),
          axios.get(`${API}/events/${event.id}/matches/upcoming`),
        ]);
        setCurrentRound(roundRes.data);
        applyUpcoming(upRes.data);
        toast.success(`Schedule created! ${res.data.total_rounds} rounds using ${res.data.effective_boards} board${res.data.effective_boards !== 1 ? "s" : ""}`);
      }
    } catch (e) { toast.error("Failed to generate schedule"); }
    finally { setLoading((p) => ({ ...p, generateSchedule: false })); }
  };

  const [matchConfirm, setMatchConfirm] = useState(null);
  const [matchConfirmWinner, setMatchConfirmWinner] = useState(null);

  const recordResult = async (matchId, winnerTeamId) => {
    // Show confirmation dialog instead of instant recording
    // Find the match from board status or current round
    let match = null;
    if (boardStatus?.active_matches) {
      match = boardStatus.active_matches.find(m => m.id === matchId);
    }
    if (!match && currentRound?.matches) {
      match = currentRound.matches.find(m => m.id === matchId);
    }
    if (match) {
      setMatchConfirm(match);
      setMatchConfirmWinner(winnerTeamId);
    } else {
      // Fallback: record directly if match not found in state
      await submitMatchResult(matchId, winnerTeamId);
    }
  };

  const submitMatchResult = async (matchId, winnerTeamId, scores) => {
    try {
      const res = await axios.put(`${API}/events/${event.id}/matches/${matchId}/result`, { winner_team_id: winnerTeamId }, authConfig());
      // Save scores if provided
      if (scores && (scores.high_score?.score || scores.low_score?.score || scores.high_checkout?.score)) {
        const scorePayload = {};
        if (scores.high_score?.player && scores.high_score?.score) scorePayload.high_score = scores.high_score;
        if (scores.low_score?.player && scores.low_score?.score) scorePayload.low_score = scores.low_score;
        if (scores.high_checkout?.player && scores.high_checkout?.score) scorePayload.high_checkout = scores.high_checkout;
        if (Object.keys(scorePayload).length > 0) {
          await axios.put(`${API}/events/${event.id}/matches/${matchId}/scores`, scorePayload, authConfig());
        }
      }
      try {
        const [bsRes, roundRes, teamsRes, upRes] = await Promise.all([
          axios.get(`${API}/events/${event.id}/matches/board-status`),
          axios.get(`${API}/events/${event.id}/matches/current-round`),
          axios.get(`${API}/events/${event.id}/teams`),
          axios.get(`${API}/events/${event.id}/matches/upcoming`),
        ]);
        setBoardStatus(bsRes.data);
        setCurrentRound(roundRes.data);
        setTeams(teamsRes.data);
        applyUpcoming(upRes.data);
      } catch (e) { /* refresh error */ }
      if (res.data.continuous) {
        toast.success("Result recorded! Click CONTINUE for next match.");
      } else {
        toast.success("Result recorded!");
      }
    } catch (e) {
      console.error("Record result error:", e.response?.data || e.message);
      toast.error(e.response?.data?.detail || "Failed to record result");
    }
    setMatchConfirm(null);
    setMatchConfirmWinner(null);
  };

  const continueBoard = async (matchId) => {
    try {
      const res = await axios.post(`${API}/events/${event.id}/matches/${matchId}/continue`, {}, authConfig());
      const [bsRes, teamsRes, upRes, roundRes] = await Promise.all([
        axios.get(`${API}/events/${event.id}/matches/board-status`),
        axios.get(`${API}/events/${event.id}/teams`),
        axios.get(`${API}/events/${event.id}/matches/upcoming`),
        axios.get(`${API}/events/${event.id}/matches/current-round`),
      ]);
      setBoardStatus(bsRes.data);
      setTeams(teamsRes.data);
      applyUpcoming(upRes.data);
      setCurrentRound(roundRes.data);
      if (res.data.round_robin_complete) {
        const evRes = await axios.get(`${API}/events/${event.id}`);
        setEvent(evRes.data);
        toast.success("All matches complete!");
      } else if (res.data.waiting) {
        toast.info("Opponents still playing. Board will update when available.");
      } else if (res.data.next_matches) {
        toast.success(`${res.data.next_matches.length} match${res.data.next_matches.length > 1 ? "es" : ""} loaded!`);
      } else {
        toast.success("Next match loaded!");
      }
    } catch (e) {
      console.error("Continue error:", e.response?.data || e.message);
      toast.error("Failed to load next match");
    }
  };

  const substitutePlayer = async () => {
    if (!subDialog.team || !subDialog.oldPlayer || !subNewPlayer.trim()) return;
    try {
      const res = await axios.put(`${API}/events/${event.id}/teams/${subDialog.team.id}/substitute`, {
        old_player: subDialog.oldPlayer, new_player: subNewPlayer.trim(),
      }, authConfig());
      toast.success(res.data.message);
      setSubDialog({ open: false, team: null, oldPlayer: "" });
      setSubNewPlayer("");
      // Refresh teams and matches
      const [teamsRes, roundRes, upRes] = await Promise.all([
        axios.get(`${API}/events/${event.id}/teams`),
        event.pace_mode === "continuous"
          ? axios.get(`${API}/events/${event.id}/matches/board-status`)
          : axios.get(`${API}/events/${event.id}/matches/current-round`),
        axios.get(`${API}/events/${event.id}/matches/upcoming`),
      ]);
      setTeams(teamsRes.data);
      if (event.pace_mode === "continuous") setBoardStatus(roundRes.data);
      else setCurrentRound(roundRes.data);
      applyUpcoming(upRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Substitution failed"); }
  };

  const removeTeam = async (team) => {
    try {
      const res = await axios.delete(`${API}/events/${event.id}/teams/${team.id}`, authConfig());
      toast.success(res.data.message);
      const [teamsRes, roundRes, upRes] = await Promise.all([
        axios.get(`${API}/events/${event.id}/teams`),
        event.pace_mode === "continuous"
          ? axios.get(`${API}/events/${event.id}/matches/board-status`)
          : axios.get(`${API}/events/${event.id}/matches/current-round`),
        axios.get(`${API}/events/${event.id}/matches/upcoming`),
      ]);
      setTeams(teamsRes.data);
      if (event.pace_mode === "continuous") setBoardStatus(roundRes.data);
      else setCurrentRound(roundRes.data);
      applyUpcoming(upRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to remove team"); }
  };

  const addTeamMidEvent = async () => {
    const isTeamsMode = event.play_mode === "teams";
    if (isTeamsMode) {
      const ts = event.team_size || 2;
      const filled = addTeamForm.teamPlayers.filter(p => p.trim()).length;
      if (filled !== ts) return;
      const missingMobile = addTeamForm.teamPlayers.some((p, i) => p.trim() && !(addTeamForm.teamMobiles[i] || "").trim());
      if (missingMobile) { toast.error("Every player needs a mobile number (1 credit each)."); return; }
    } else {
      if (!addTeamForm.player1.trim() || !addTeamForm.player1_mobile.trim()) { toast.error("Player 1 needs a name and mobile number."); return; }
      if (event.play_mode === "doubles" && (!addTeamForm.player2.trim() || !addTeamForm.player2_mobile.trim())) { toast.error("Player 2 needs a name and mobile number."); return; }
    }
    try {
      const payload = isTeamsMode
        ? {
            team_name: addTeamForm.team_name,
            players: addTeamForm.teamPlayers.map((name, i) => ({ name, mobile: addTeamForm.teamMobiles[i] || "" })).filter(p => p.name.trim()),
          }
        : {
            player1: addTeamForm.player1, player1_mobile: addTeamForm.player1_mobile,
            player2: addTeamForm.player2, player2_mobile: addTeamForm.player2_mobile,
            team_name: addTeamForm.team_name,
          };
      const res = await axios.post(`${API}/events/${event.id}/teams/add`, payload, authConfig());
      toast.success(res.data.message);
      setShowAddTeam(false);
      setAddTeamForm({ player1: "", player1_mobile: "", player2: "", player2_mobile: "", team_name: "", teamPlayers: [], teamMobiles: [] });
      // Full refresh
      const [teamsRes, upRes, bsRes, roundRes] = await Promise.all([
        axios.get(`${API}/events/${event.id}/teams`),
        axios.get(`${API}/events/${event.id}/matches/upcoming`),
        axios.get(`${API}/events/${event.id}/matches/board-status`),
        axios.get(`${API}/events/${event.id}/matches/current-round`),
      ]);
      setTeams(teamsRes.data);
      setNextUpMatches(upRes.data?.next_up || []);
      setUpcomingMatches(upRes.data?.waiting || []);
      setAvailableBoards(upRes.data?.available_boards || 0);
      setBoardStatus(bsRes.data);
      setCurrentRound(roundRes.data);
      const vid = localStorage.getItem("darts_venue_id") || "";
      const rosterRes = await axios.get(`${API}/roster${vid ? `?venue=${vid}` : ""}`);
      setRoster(rosterRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add team"); }
  };

  // Team Builder functions (for "teams" play mode). Each entry is {name, mobile}
  // since every player spends their own credit to join.
  const addPlayerToTeamBuilder = (name, mobile = "") => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const ts = event?.team_size || teamSize;
    if (teamBuilderPlayers.length >= ts) {
      toast.error(`Team is full (${ts} players)`);
      return;
    }
    if (teamBuilderPlayers.some((p) => p.name.toLowerCase() === trimmed.toLowerCase())) {
      toast.error("Player already added to this team");
      return;
    }
    setTeamBuilderPlayers((prev) => [...prev, { name: trimmed, mobile: mobile.trim() }]);
    setTeamBuilderInput("");
    setTeamBuilderMobile("");
    setTeamBuilderSuggestions([]);
  };

  const removePlayerFromTeamBuilder = (idx) => {
    setTeamBuilderPlayers((prev) => prev.filter((_, i) => i !== idx));
  };

  const saveTeamFromBuilder = async () => {
    const ts = event?.team_size || teamSize;
    if (teamBuilderPlayers.length !== ts) {
      toast.error(`Team needs exactly ${ts} players`);
      return;
    }
    const missingMobile = teamBuilderPlayers.filter((p) => !p.mobile);
    if (missingMobile.length > 0) {
      toast.error(`Missing mobile number for: ${missingMobile.map((p) => p.name).join(", ")}`);
      return;
    }
    try {
      const defaultName = teamBuilderName.trim() || `Team ${teams.filter(t => !t.removed).length + 1}`;
      const res = await axios.post(`${API}/events/${event.id}/teams/add-manual`, {
        team_name: defaultName,
        players: teamBuilderPlayers,
      }, authConfig());
      toast.success(`${res.data.team_name} added!`);
      setTeamBuilderPlayers([]);
      setTeamBuilderName("");
      setTeamBuilderInput("");
      setTeamBuilderMobile("");
      // Refresh teams
      const [teamsRes, eventRes] = await Promise.all([
        axios.get(`${API}/events/${event.id}/teams`),
        axios.get(`${API}/events/${event.id}`),
      ]);
      setTeams(teamsRes.data);
      setEvent(eventRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add team"); }
  };

  const removeSetupTeam = async (team) => {
    if (!window.confirm(`Remove ${team.team_name}?`)) return;
    try {
      await axios.delete(`${API}/events/${event.id}/teams/${team.id}/setup-remove`, authConfig());
      const [teamsRes, eventRes] = await Promise.all([
        axios.get(`${API}/events/${event.id}/teams`),
        axios.get(`${API}/events/${event.id}`),
      ]);
      setTeams(teamsRes.data);
      setEvent(eventRes.data);
      toast.success("Team removed");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };




  const advanceRound = async () => {
    setLoading((p) => ({ ...p, advanceRound: true }));
    try {
      const res = await axios.post(`${API}/events/${event.id}/advance-round`, {}, authConfig());
      if (res.data.needs_divisions) {
        const eventRes = await axios.get(`${API}/events/${event.id}`);
        setEvent(eventRes.data);
        toast.success("Round-robin complete! Generate divisions to start knockout phase.");
      } else {
        const [roundRes, eventRes, upRes] = await Promise.all([
          axios.get(`${API}/events/${event.id}/matches/current-round`),
          axios.get(`${API}/events/${event.id}`),
          axios.get(`${API}/events/${event.id}/matches/upcoming`),
        ]);
        setCurrentRound(roundRes.data);
        setEvent(eventRes.data);
        applyUpcoming(upRes.data);
        toast.success("Advanced to next round!");
      }
    } catch (e) { toast.error("Failed to advance round"); }
    finally { setLoading((p) => ({ ...p, advanceRound: false })); }
  };

  const endEvent = async () => {
    try {
      await axios.put(`${API}/events/${event.id}/complete`, {}, authConfig());
      setEvent((prev) => ({ ...prev, status: "completed" }));
      toast.success("Event ended!");
    } catch (e) { toast.error("Failed"); }
  };

  const rematchEvent = async () => {
    setLoading((p) => ({ ...p, rematch: true }));
    try {
      const res = await axios.post(`${API}/events/${event.id}/rematch`, {}, authConfig());
      const newEvent = res.data.event;
      setEvent(newEvent);
      setPlayers([]);
      setTeams([]);
      setCurrentRound(null);
      setReservePlayer(null);
      setTournamentState(null);
      setBoardStatus(null);
      setUpcomingMatches([]);
      toast.success(res.data.message);
      // Load the new event's teams
      const teamsRes = await axios.get(`${API}/events/${newEvent.id}/teams`);
      setTeams(teamsRes.data);
      // Auto-switch to matches tab so admin can generate matches
      setActiveTab("matches");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create rematch"); }
    finally { setLoading((p) => ({ ...p, rematch: false })); }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/events/${event.id}/stats/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `darts_stats_${event.name.replace(/\s+/g, "_")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch (e) { toast.error("Failed to download PDF"); }
  };

  const logout = () => {
    localStorage.removeItem("darts_token");
    localStorage.removeItem("darts_username");
    localStorage.removeItem("darts_role");
    localStorage.removeItem("darts_venue_id");
    localStorage.removeItem("darts_venue_name");
    navigate("/admin/login");
  };

  // --- Admin Management (Super Admin) ---
  const loadPendingAdmins = async () => {
    try {
      const res = await axios.get(`${API}/admin/pending`, authConfig());
      setPendingAdmins(res.data);
    } catch (e) { console.error(e); }
  };

  const loadAllAdmins = async () => {
    try {
      const res = await axios.get(`${API}/admin/list`, authConfig());
      setAllAdmins(res.data);
    } catch (e) { console.error(e); }
  };

  const approveAdmin = async (adminId) => {
    try {
      await axios.put(`${API}/admin/${adminId}/approve`, {}, authConfig());
      toast.success("Admin approved!");
      loadPendingAdmins();
      loadAllAdmins();
    } catch (e) { toast.error("Failed to approve"); }
  };

  const rejectAdmin = async (adminId) => {
    try {
      await axios.put(`${API}/admin/${adminId}/reject`, {}, authConfig());
      toast.success("Admin rejected");
      loadPendingAdmins();
      loadAllAdmins();
    } catch (e) { toast.error("Failed to reject"); }
  };

  const toggleAdminStatus = async (adminId, currentStatus) => {
    try {
      const endpoint = currentStatus === "approved" ? "deactivate" : "activate";
      await axios.put(`${API}/admin/${adminId}/${endpoint}`, {}, authConfig());
      toast.success(`Admin ${endpoint}d`);
      loadAllAdmins();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const forceResetPassword = async (adminId) => {
    try {
      const res = await axios.post(`${API}/admin/${adminId}/reset-password`, {}, authConfig());
      setResetPwResult(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reset password"); }
  };

  const loadAllVenues = async () => {
    try {
      const res = await axios.get(`${API}/admin/venues`, authConfig());
      setAllVenues(res.data);
    } catch (e) { console.error(e); }
  };

  const loadVenueBoards = async (vid) => {
    const targetVid = vid || venueId;
    if (!targetVid) return;
    try {
      const res = await axios.get(`${API}/venues/${targetVid}/boards`);
      setVenueBoards(res.data);
      const validIds = new Set(res.data.map(b => b.id));
      setSelectedBoardIds(prev => {
        // Keep only IDs that belong to this venue's boards
        const filtered = new Set([...prev].filter(id => validIds.has(id)));
        // If nothing remains, pre-select first 4
        if (filtered.size === 0 && res.data.length > 0) {
          return new Set(res.data.slice(0, 4).map(b => b.id));
        }
        return filtered;
      });
    } catch (e) { console.error(e); }
  };

  const deleteVenue = async (venueTarget) => {
    if (!window.confirm(`PERMANENTLY DELETE venue "${venueTarget.name}" and ALL its data (events, matches, teams, admins, players)?\n\nThis cannot be undone.`)) return;
    if (!window.confirm(`Are you absolutely sure? This will delete everything for "${venueTarget.name}".`)) return;
    try {
      const res = await axios.delete(`${API}/admin/venues/${venueTarget.id}`, authConfig());
      toast.success(res.data.message);
      loadAllVenues();
      loadAllAdmins();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete venue"); }
  };

  // --- Season Management (App Manager) ---
  const loadAdminRoster = async () => {
    try {
      const res = await axios.get(`${API}/admin/roster`, authConfig());
      setAdminRoster(res.data);
    } catch (e) { console.error(e); }
  };

  const deleteRosterPlayer = async (playerId, playerName) => {
    try {
      await axios.delete(`${API}/admin/roster/${playerId}`, authConfig());
      setAdminRoster((prev) => prev.filter((p) => p.id !== playerId));
      toast.success(`${playerName} removed from roster`);
    } catch (e) { toast.error("Failed to remove player"); }
  };

  const clearSeason = async () => {
    setLoading((p) => ({ ...p, clearSeason: true }));
    try {
      const res = await axios.post(`${API}/admin/clear-season`, {}, authConfig());
      toast.success(res.data.message);
      setEvent(null); setPlayers([]); setTeams([]); setCurrentRound(null);
      setTournamentState(null); setReservePlayer(null); setPastEvents([]);
      setCareerStats([]);
    } catch (e) { toast.error("Failed to clear season"); }
    finally { setLoading((p) => ({ ...p, clearSeason: false })); }
  };

  const factoryReset = async () => {
    setLoading((p) => ({ ...p, factoryReset: true }));
    try {
      const res = await axios.post(`${API}/admin/factory-reset`, {}, authConfig());
      toast.success(res.data.message);
      setEvent(null); setPlayers([]); setTeams([]); setCurrentRound(null);
      setTournamentState(null); setReservePlayer(null); setPastEvents([]);
      setCareerStats([]); setAdminRoster([]);
    } catch (e) { toast.error("Failed to reset"); }
    finally { setLoading((p) => ({ ...p, factoryReset: false })); }
  };

  // --- Event History ---
  const loadPastEvents = async () => {
    try {
      const vid = localStorage.getItem("darts_venue_id") || "";
      const res = await axios.get(`${API}/events${vid ? `?venue=${vid}` : ""}`);
      setPastEvents(res.data);
    } catch (e) { console.error(e); }
  };

  const loadHistoryStats = async (eventId) => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/stats`);
      setHistoryStats(res.data);
    } catch (e) { toast.error("Failed to load stats"); }
  };

  const deleteEvent = async (eventId, eventName) => {
    try {
      await axios.delete(`${API}/events/${eventId}`, authConfig());
      toast.success(`"${eventName}" deleted`);
      setPastEvents((prev) => prev.filter((e) => e.id !== eventId));
      if (historyStats?.event?.id === eventId) setHistoryStats(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete event"); }
  };

  const downloadEventPDF = async (eventId, eventName) => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/stats/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `darts_stats_${eventName.replace(/\s+/g, "_")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch (e) { toast.error("Failed to download PDF"); }
  };

  // --- Career Stats ---
  const loadCareerStats = async () => {
    try {
      const url = venueId ? `${API}/players/career-stats?venue=${venueId}` : `${API}/players/career-stats`;
      const res = await axios.get(url);
      setCareerStats(res.data);
    } catch (e) { console.error(e); }
  };

  // --- Player Roster Database ---
  const loadRoster = async () => {
    try {
      const vid = localStorage.getItem("darts_venue_id") || "";
      const res = await axios.get(`${API}/roster${vid ? `?venue=${vid}` : ""}`);
      setRoster(res.data);
    } catch (e) { console.error(e); }
  };

  const [rosterForTeamBuilder, setRosterForTeamBuilder] = useState(false);

  const openRosterDialog = () => {
    loadRoster();
    setSelectedRoster(new Set());
    setRosterSearch("");
    setRosterForTeamBuilder(false);
    setShowRoster(true);
  };

  const openRosterForTeamBuilder = () => {
    loadRoster();
    setSelectedRoster(new Set());
    setRosterSearch("");
    setRosterForTeamBuilder(true);
    setShowRoster(true);
  };

  const toggleRosterPlayer = (name) => {
    setSelectedRoster((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const addSelectedFromRoster = async () => {
    if (selectedRoster.size === 0 || !event) return;
    const names = [...selectedRoster];
    const rosterByName = new Map(roster.map((r) => [r.name, r]));
    const withMobile = names.filter((n) => rosterByName.get(n)?.mobile);
    const withoutMobile = names.filter((n) => !rosterByName.get(n)?.mobile);
    if (withoutMobile.length > 0) {
      toast.info(`${withoutMobile.join(", ")} ${withoutMobile.length === 1 ? "has" : "have"} no mobile on file — add ${withoutMobile.length === 1 ? "them" : "them"} one at a time so we know whose credit to spend.`);
    }
    if (withMobile.length === 0) {
      setShowRoster(false);
      setSelectedRoster(new Set());
      setRosterForTeamBuilder(false);
      return;
    }
    const teamBuilderNames = new Set(teamBuilderPlayers.map((p) => p.name));
    if (rosterForTeamBuilder) {
      const ts = event?.team_size || teamSize;
      const toAdd = withMobile
        .filter((n) => !teamBuilderNames.has(n))
        .slice(0, ts - teamBuilderPlayers.length)
        .map((n) => ({ name: n, mobile: rosterByName.get(n).mobile }));
      setTeamBuilderPlayers((prev) => [...prev, ...toAdd]);
      setShowRoster(false);
      setSelectedRoster(new Set());
      setRosterForTeamBuilder(false);
      toast.success(`${toAdd.length} player${toAdd.length !== 1 ? "s" : ""} added to team!`);
      return;
    }
    setLoading((p) => ({ ...p, addPlayer: true }));
    try {
      const playersPayload = withMobile.map((n) => ({ name: n, mobile: rosterByName.get(n).mobile }));
      const res = await axios.post(`${API}/events/${event.id}/players`, { players: playersPayload }, authConfig());
      setPlayers((prev) => [...prev, ...res.data]);
      setShowRoster(false);
      setSelectedRoster(new Set());
      toast.success(`${res.data.length} player${res.data.length !== 1 ? "s" : ""} added from database! (1 credit each)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add players"); }
    finally { setLoading((p) => ({ ...p, addPlayer: false })); }
  };

  const handlePlayerNameChange = async (value) => {
    setNewPlayerName(value);
    if (value.trim().length >= 2) {
      try {
        const res = await axios.get(`${API}/roster?q=${encodeURIComponent(value.trim())}`);
        const currentNames = new Set(players.map((p) => p.name.toLowerCase()));
        setSuggestions(res.data.filter((r) => !currentNames.has(r.name.toLowerCase())).slice(0, 5));
      } catch (e) { setSuggestions([]); }
    } else {
      setSuggestions([]);
    }
  };

  const addSuggestedPlayer = async (s) => {
    setSuggestions([]);
    if (!event) return;
    if (!s.mobile) {
      // No mobile on file — each player needs their own credit, so we need a
      // number to charge. Prefill the name and let the admin type it in.
      setNewPlayerName(s.name);
      setNewPlayerMobile("");
      toast.info(`${s.name} has no mobile number on file — enter one to add them.`);
      return;
    }
    setNewPlayerName(""); setNewPlayerMobile("");
    try {
      const res = await axios.post(`${API}/events/${event.id}/players`, { players: [{ name: s.name, mobile: s.mobile }] }, authConfig());
      setPlayers((prev) => [...prev, ...res.data]);
      toast.success("Player added! (1 credit used)");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // --- Tournament ---
  const loadTournamentState = async () => {
    if (!event) return;
    try {
      const res = await axios.get(`${API}/events/${event.id}/tournament/state`);
      setTournamentState(res.data);
    } catch (e) { console.error(e); }
  };

  const generateDivisions = async () => {
    if (!event) return;
    setLoading((p) => ({ ...p, divisions: true }));
    try {
      await axios.post(`${API}/events/${event.id}/tournament/generate-divisions`, {}, authConfig());
      await loadTournamentState();
      const eventRes = await axios.get(`${API}/events/${event.id}`);
      setEvent(eventRes.data);
      toast.success("Divisions generated! Knockout phase begins.");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading((p) => ({ ...p, divisions: false })); }
  };

  const advanceBracket = async () => {
    if (!event) return;
    setLoading((p) => ({ ...p, advanceBracket: true }));
    try {
      const res = await axios.post(`${API}/events/${event.id}/tournament/advance-bracket`, {}, authConfig());
      await loadTournamentState();
      const eventRes = await axios.get(`${API}/events/${event.id}`);
      setEvent(eventRes.data);
      if (res.data.tournament_complete) toast.success("Tournament complete! Champions crowned!");
      else toast.success("Advanced to next bracket round!");
    } catch (e) { toast.error("Failed to advance bracket"); }
    finally { setLoading((p) => ({ ...p, advanceBracket: false })); }
  };

  const recordBracketResult = async (matchId, winnerTeamId) => {
    // For knockout events, show confirmation dialog first
    if (event?.event_type === "knockout") {
      const match = (knockoutState?.matches || []).find(m => m.id === matchId);
      if (match) {
        setKoConfirmMatch(match);
        setKoConfirmWinner(winnerTeamId);
        return;
      }
    }
    await submitBracketResult(matchId, winnerTeamId);
  };

  const submitBracketResult = async (matchId, winnerTeamId, scores) => {
    try {
      await axios.put(`${API}/events/${event.id}/matches/${matchId}/result`, { winner_team_id: winnerTeamId }, authConfig());
      // Save scores if provided
      if (scores && (scores.high_score?.score || scores.low_score?.score || scores.high_checkout?.score)) {
        const scorePayload = {};
        if (scores.high_score?.player && scores.high_score?.score) scorePayload.high_score = scores.high_score;
        if (scores.low_score?.player && scores.low_score?.score) scorePayload.low_score = scores.low_score;
        if (scores.high_checkout?.player && scores.high_checkout?.score) scorePayload.high_checkout = scores.high_checkout;
        if (Object.keys(scorePayload).length > 0) {
          await axios.put(`${API}/events/${event.id}/matches/${matchId}/scores`, scorePayload, authConfig());
        }
      }
      try {
        if (event.event_type === "knockout") await loadKnockoutState();
        else await loadTournamentState();
        const teamsRes = await axios.get(`${API}/events/${event.id}/teams`);
        setTeams(teamsRes.data);
        const eventRes = await axios.get(`${API}/events/${event.id}`);
        setEvent(eventRes.data);
      } catch (refreshErr) { console.error("Refresh error", refreshErr); }
      toast.success("Result recorded!");
    } catch (e) {
      console.error("Bracket result error:", e.response?.data || e.message);
      toast.error(e.response?.data?.detail || "Failed to record result");
    }
    setKoConfirmMatch(null);
    setKoConfirmWinner(null);
  };

  // Knockout functions
  const loadKnockoutState = async () => {
    if (!event) return;
    try {
      const res = await axios.get(`${API}/events/${event.id}/knockout/state`);
      setKnockoutState(res.data);
    } catch (e) { console.error(e); }
  };

  const generateKnockoutBracket = async () => {
    if (!event) return;
    setLoading((p) => ({ ...p, generateKnockout: true }));
    try {
      await axios.post(`${API}/events/${event.id}/knockout/generate`, {}, authConfig());
      await loadKnockoutState();
      const eventRes = await axios.get(`${API}/events/${event.id}`);
      setEvent(eventRes.data);
      const teamsRes = await axios.get(`${API}/events/${event.id}/teams`);
      setTeams(teamsRes.data);
      toast.success("Knockout bracket generated!");
      setActiveTab("matches");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to generate bracket"); }
    finally { setLoading((p) => ({ ...p, generateKnockout: false })); }
  };

  const openScoreDialog = (match) => {
    setScoreForm({
      hsPlayer: match.high_score?.player || "",
      hsValue: match.high_score?.score?.toString() || "",
      lsPlayer: match.low_score?.player || "",
      lsValue: match.low_score?.score?.toString() || "",
      hcPlayer: match.high_checkout?.player || "",
      hcValue: match.high_checkout?.score?.toString() || "",
    });
    setScoreDialog({ open: true, match });
  };

  const submitScores = async () => {
    const m = scoreDialog.match;
    if (!m || !event) return;
    try {
      await axios.put(`${API}/events/${event.id}/matches/${m.id}/scores`, {
        high_score_player: scoreForm.hsPlayer || null,
        high_score_value: scoreForm.hsValue ? parseInt(scoreForm.hsValue) : null,
        low_score_player: scoreForm.lsPlayer || null,
        low_score_value: scoreForm.lsValue ? parseInt(scoreForm.lsValue) : null,
        high_checkout_player: scoreForm.hcPlayer || null,
        high_checkout_value: scoreForm.hcValue ? parseInt(scoreForm.hcValue) : null,
      }, authConfig());
      toast.success("Scores saved!");
      setScoreDialog({ open: false, match: null });
      // Refresh data
      const [roundRes, teamsRes] = await Promise.all([
        axios.get(`${API}/events/${event.id}/matches/current-round`),
        axios.get(`${API}/events/${event.id}/teams`),
      ]);
      setCurrentRound(roundRes.data);
      setTeams(teamsRes.data);
      if (event.event_type === "tournament") loadTournamentState();
      if (event.event_type === "knockout") loadKnockoutState();
    } catch (e) { toast.error("Failed to save scores"); }
  };

  const roundComplete = currentRound?.matches?.length > 0 && currentRound?.matches?.every((m) => m.status === "completed");

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0A0A0A] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Target className="w-6 h-6 text-[#E3292E]" />
            <span className="font-display text-xl text-white uppercase tracking-tight hidden sm:inline">Darts Manager</span>
            {venueName && (
              <Badge className="bg-white/10 text-white/60 rounded-none text-xs ml-2 hidden md:inline-flex">{venueName}</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setShowInvite(true)} variant="ghost" className="text-white/50 hover:text-white hover:bg-white/5 rounded-none text-sm" data-testid="invite-btn">
              <UserPlus className="mr-1 h-4 w-4" /> <span className="hidden sm:inline">Invite</span>
            </Button>
            <Button onClick={() => setShowQR(true)} variant="ghost" className="text-white/50 hover:text-white hover:bg-white/5 rounded-none text-sm" data-testid="show-qr-btn">
              <QrCode className="mr-1 h-4 w-4" /> <span className="hidden sm:inline">QR</span>
            </Button>
            <Button onClick={() => setShowGrantCredits(true)} variant="ghost" className="text-white/50 hover:text-white hover:bg-white/5 rounded-none text-sm" data-testid="grant-credits-btn">
              <Coins className="mr-1 h-4 w-4" /> <span className="hidden sm:inline">Credits</span>
            </Button>
            <Button onClick={() => navigate("/scoreboard")} variant="ghost" className="text-white/50 hover:text-white hover:bg-white/5 rounded-none text-sm" data-testid="view-scoreboard-link">
              <Eye className="mr-1 h-4 w-4" /> <span className="hidden sm:inline">Scoreboard</span>
            </Button>
            <Button onClick={() => setShowGuide(true)} variant="ghost" className="text-white/50 hover:text-white hover:bg-white/5 rounded-none text-sm" data-testid="setup-guide-btn">
              <BookOpen className="mr-1 h-4 w-4" /> <span className="hidden sm:inline">Guide</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-none border-white/20 text-white hover:bg-white/10 text-sm" data-testid="admin-menu-btn">
                  <User className="mr-1 h-4 w-4" /> <span className="hidden sm:inline">{localStorage.getItem("darts_username") || "Admin"}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="rounded-none border-white/20 bg-[#141414]">
                <DropdownMenuItem onClick={logout} className="text-white/60 hover:text-white focus:text-white focus:bg-white/10 cursor-pointer" data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#141414] border border-white/10 rounded-none p-1 w-full sm:w-auto flex-wrap">
            <TabsTrigger value="setup" onClick={() => { if (role === "super_admin") loadAllVenues(); }} className="rounded-none data-[state=active]:bg-[#E3292E] data-[state=active]:text-white text-white/50 font-heading text-sm" data-testid="tab-setup">
              Setup
            </TabsTrigger>
            <TabsTrigger value="matches" className="rounded-none data-[state=active]:bg-[#E3292E] data-[state=active]:text-white text-white/50 font-heading text-sm" data-testid="tab-matches">
              Matches
            </TabsTrigger>
            <TabsTrigger value="stats" onClick={loadCareerStats} className="rounded-none data-[state=active]:bg-[#E3292E] data-[state=active]:text-white text-white/50 font-heading text-sm" data-testid="tab-stats">
              Stats
            </TabsTrigger>
            <TabsTrigger value="history" onClick={loadPastEvents} className="rounded-none data-[state=active]:bg-[#E3292E] data-[state=active]:text-white text-white/50 font-heading text-sm" data-testid="tab-history">
              <History className="mr-1 h-3.5 w-3.5" /> History
            </TabsTrigger>
            {role === "super_admin" && (
              <TabsTrigger value="manage" onClick={() => { loadPendingAdmins(); loadAllAdmins(); loadAdminRoster(); loadAllVenues(); loadVenueBoards(); }} className="rounded-none data-[state=active]:bg-[#E3292E] data-[state=active]:text-white text-white/50 font-heading text-sm" data-testid="tab-manage">
                <Shield className="mr-1 h-3.5 w-3.5" /> Manage
              </TabsTrigger>
            )}
          </TabsList>

          {/* ===== SETUP TAB ===== */}
          <TabsContent value="setup" className="space-y-6">
            {/* Completed event → New Event */}
            {event && event.status === "completed" && (
              <Card className="bg-[#141414] border-white/10 rounded-none">
                <CardContent className="py-8 text-center">
                  <CheckCircle className="w-12 h-12 text-[#309F6A] mx-auto mb-4" />
                  <p className="text-white font-heading font-bold text-xl mb-2">Event Complete</p>
                  <p className="text-white/50 mb-6 font-body">View stats, rematch with same teams, or start fresh</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button onClick={rematchEvent} disabled={loading.rematch} className="bg-[#309F6A] hover:bg-[#28875a] text-white rounded-none font-heading transition-colors duration-200" data-testid="rematch-btn">
                      <RotateCcw className="mr-2 h-4 w-4" /> REMATCH
                    </Button>
                    <Button onClick={() => { setEvent(null); setPlayers([]); setTeams([]); setCurrentRound(null); setReservePlayer(null); setTournamentState(null); setBoardStatus(null); setUpcomingMatches([]); setNumBoards(4); }} className="bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none font-heading transition-colors duration-200" data-testid="new-event-btn">
                      <Plus className="mr-2 h-4 w-4" /> NEW EVENT
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Create Event Form */}
            {!event && (
              <Card className="bg-[#141414] border-white/10 rounded-none">
                <CardHeader>
                  <CardTitle className="font-heading text-white text-xl">Create Event</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {role === "super_admin" && (
                    <div className="space-y-2" data-testid="super-admin-venue-picker">
                      <Label className="text-white/60 text-xs tracking-[0.2em] uppercase font-bold">Select Venue</Label>
                      <select
                        value={superAdminVenueId}
                        onChange={(e) => { setSuperAdminVenueId(e.target.value); if (e.target.value) loadVenueBoards(e.target.value); }}
                        className="w-full bg-transparent border border-white/20 text-white p-2 text-sm focus:outline-none focus:border-[#E3292E]"
                        data-testid="venue-select"
                      >
                        <option value="" className="bg-[#141414]">-- Choose a venue --</option>
                        {allVenues.map((v) => (
                          <option key={v.id} value={v.id} className="bg-[#141414]">{v.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-white/60 text-xs tracking-[0.2em] uppercase font-bold">Event Name</Label>
                    <Input value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder="e.g. Friday Night Darts" className="bg-transparent border-white/20 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#E3292E]" data-testid="event-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/60 text-xs tracking-[0.2em] uppercase font-bold">Event Type</Label>
                    <div className="flex gap-3">
                      <button onClick={() => setEventType("social")} className={`flex-1 p-3 border text-sm font-heading font-bold text-center transition-colors duration-200 ${eventType === "social" ? "border-[#E3292E] bg-[#E3292E]/10 text-white" : "border-white/20 text-white/50 hover:bg-white/5"}`} data-testid="event-type-social">
                        <Users className="w-5 h-5 mx-auto mb-1" />SOCIAL
                        <p className="text-xs font-body font-normal text-white/40 mt-1">Round-robin play</p>
                      </button>
                      <button onClick={() => setEventType("tournament")} className={`flex-1 p-3 border text-sm font-heading font-bold text-center transition-colors duration-200 ${eventType === "tournament" ? "border-[#E3292E] bg-[#E3292E]/10 text-white" : "border-white/20 text-white/50 hover:bg-white/5"}`} data-testid="event-type-tournament">
                        <Trophy className="w-5 h-5 mx-auto mb-1" />TOURNAMENT
                        <p className="text-xs font-body font-normal text-white/40 mt-1">Round-robin + divisions</p>
                      </button>
                      <button onClick={() => setEventType("knockout")} className={`flex-1 p-3 border text-sm font-heading font-bold text-center transition-colors duration-200 ${eventType === "knockout" ? "border-[#E3292E] bg-[#E3292E]/10 text-white" : "border-white/20 text-white/50 hover:bg-white/5"}`} data-testid="event-type-knockout">
                        <Swords className="w-5 h-5 mx-auto mb-1" />KNOCKOUT
                        <p className="text-xs font-body font-normal text-white/40 mt-1">Single elimination bracket</p>
                      </button>
                    </div>
                    {eventType === "knockout" && (
                      <div className="mt-3 p-3 border border-white/10 bg-white/[0.02]" data-testid="knockout-guide">
                        <p className="text-white/60 text-xs font-body mb-2">Best team counts for a clean bracket (no byes):</p>
                        <div className="flex gap-2 flex-wrap">
                          {[4, 8, 16, 32].map((n) => (
                            <span key={n} className="px-2.5 py-1 border border-[#309F6A]/30 bg-[#309F6A]/5 text-[#309F6A] text-xs font-heading font-bold">{n} {playMode === "singles" ? "players" : "teams"}</span>
                          ))}
                        </div>
                        <p className="text-white/30 text-[10px] font-body mt-2">Other counts work but some {playMode === "singles" ? "players" : "teams"} will get a first-round bye (auto-advance).</p>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white/60 text-xs tracking-[0.2em] uppercase font-bold">Game Format</Label>
                      <select value={gameFormat} onChange={(e) => setGameFormat(e.target.value)} className="w-full bg-transparent border border-white/20 text-white text-sm font-heading font-bold px-3 py-2.5 focus:outline-none focus:border-[#E3292E]" data-testid="format-select">
                        {[101,201,301,401,501,601,701,801,901,1001].map((f) => (
                          <option key={f} value={String(f)} className="bg-[#141414]">{f}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/60 text-xs tracking-[0.2em] uppercase font-bold">Play Mode</Label>
                      <div className="flex gap-2">
                        <button onClick={() => setPlayMode("doubles")} className={`flex-1 py-2.5 border text-sm font-heading font-bold text-center transition-colors duration-200 ${playMode === "doubles" ? "border-[#E3292E] bg-[#E3292E]/10 text-white" : "border-white/20 text-white/50 hover:bg-white/5"}`} data-testid="mode-doubles">
                          DOUBLES
                        </button>
                        <button onClick={() => setPlayMode("singles")} className={`flex-1 py-2.5 border text-sm font-heading font-bold text-center transition-colors duration-200 ${playMode === "singles" ? "border-[#E3292E] bg-[#E3292E]/10 text-white" : "border-white/20 text-white/50 hover:bg-white/5"}`} data-testid="mode-singles">
                          SINGLES
                        </button>
                        <button onClick={() => setPlayMode("teams")} className={`flex-1 py-2.5 border text-sm font-heading font-bold text-center transition-colors duration-200 ${playMode === "teams" ? "border-[#E3292E] bg-[#E3292E]/10 text-white" : "border-white/20 text-white/50 hover:bg-white/5"}`} data-testid="mode-teams">
                          TEAMS
                        </button>
                      </div>
                      {playMode === "teams" && (
                        <div className="flex items-center gap-3 pt-2">
                          <Label className="text-white/40 text-xs whitespace-nowrap">Players per team:</Label>
                          <Select value={String(teamSize)} onValueChange={(v) => setTeamSize(parseInt(v))}>
                            <SelectTrigger className="w-20 bg-transparent border-white/20 text-white h-9 rounded-none" data-testid="team-size-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1a1a1a] border-white/10 rounded-none">
                              {[2,3,4,5,6,7,8,9,10].map((n) => (
                                <SelectItem key={n} value={String(n)} className="text-white hover:bg-white/10 rounded-none">{n}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/60 text-xs tracking-[0.2em] uppercase font-bold">Play Pace</Label>
                    <div className="flex gap-2">
                      <button onClick={() => setPaceMode("round_by_round")} className={`flex-1 py-2.5 px-2 border text-xs font-heading font-bold text-center transition-colors duration-200 ${paceMode === "round_by_round" ? "border-[#E3292E] bg-[#E3292E]/10 text-white" : "border-white/20 text-white/50 hover:bg-white/5"}`} data-testid="pace-round">
                        ROUND-BY-ROUND
                        <p className="text-[10px] font-body font-normal text-white/40 mt-0.5">All boards finish before next round</p>
                      </button>
                      <button onClick={() => setPaceMode("continuous")} className={`flex-1 py-2.5 px-2 border text-xs font-heading font-bold text-center transition-colors duration-200 ${paceMode === "continuous" ? "border-[#E3292E] bg-[#E3292E]/10 text-white" : "border-white/20 text-white/50 hover:bg-white/5"}`} data-testid="pace-continuous">
                        CONTINUOUS PLAY
                        <p className="text-[10px] font-body font-normal text-white/40 mt-0.5">Next match auto-loads on each board</p>
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/60 text-xs tracking-[0.2em] uppercase font-bold">Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left rounded-none border-white/20 bg-transparent text-white hover:bg-white/10" data-testid="event-date-btn">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {eventDate ? format(eventDate, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 rounded-none border-white/20 bg-[#141414] z-[100]" data-testid="calendar-popover" align="start">
                        <Calendar mode="single" selected={eventDate} onSelect={(d) => { if (d) setEventDate(d); }} initialFocus className="bg-[#141414] text-white" />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/60 text-xs tracking-[0.2em] uppercase font-bold">Select Dartboards</Label>
                    <div className="grid grid-cols-5 gap-2">
                      {venueBoards.map((b) => {
                        const sel = selectedBoardIds.has(b.id);
                        return (
                          <button key={b.id} type="button" onClick={() => { const next = new Set(selectedBoardIds); if (sel) next.delete(b.id); else next.add(b.id); setSelectedBoardIds(next); }} className={`py-2.5 px-1 border text-xs font-heading font-bold text-center transition-colors duration-200 ${sel ? "border-[#E3292E] bg-[#E3292E]/20 text-white" : "border-white/10 text-white/30 hover:bg-white/5"}`} data-testid={`board-select-${b.board_number}`}>
                            {b.board_number}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-white/30 text-xs font-body">{selectedBoardIds.size} board{selectedBoardIds.size !== 1 ? "s" : ""} selected &middot; Tap to toggle</p>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button disabled={!eventName.trim() || selectedBoardIds.size < 1 || loading.createEvent || (role === "super_admin" && !superAdminVenueId)} className="bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none w-full font-heading font-bold transition-colors duration-200" data-testid="create-event-btn">
                        <Plus className="mr-2 h-4 w-4" /> CREATE EVENT
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#141414] border-white/10 rounded-none">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading text-white text-xl">Confirm Selected Boards</AlertDialogTitle>
                        <AlertDialogDescription className="text-white/50 font-body space-y-3">
                          <span className="block">You're about to create <span className="text-white font-bold">{eventName}</span> using <span className="text-white font-bold text-lg">{selectedBoardIds.size} dartboard{selectedBoardIds.size !== 1 ? "s" : ""}</span>.</span>
                          <span className="block">Boards: {venueBoards.filter(b => selectedBoardIds.has(b.id)).map(b => b.board_number).sort((a,b)=>a-b).join(", ")}</span>
                          <span className="block text-white/30 text-xs">If this is incorrect, go back and update the board selection before creating the event.</span>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none border-white/20 text-white hover:bg-white/10" data-testid="confirm-create-cancel">Go Back</AlertDialogCancel>
                        <AlertDialogAction onClick={createEvent} className="bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none font-heading font-bold" data-testid="confirm-create-event">
                          Confirm &amp; Create Event
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </CardContent>
              </Card>
            )}

            {/* Active event management */}
            {event && event.status !== "completed" && (
              <>
                {/* Event Info */}
                <div className="border border-white/10 bg-[#141414] p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase text-[#E3292E] font-bold">Current Event</p>
                    <p className="text-white font-heading font-bold text-xl mt-1">{event.name}</p>
                    <p className="text-white/50 text-sm font-body">{event.date}</p>
                    <p className="text-white/30 text-xs font-body mt-1">
                      {event.game_format || "501"} - {event.play_mode === "singles" ? "Singles" : event.play_mode === "teams" ? "Teams" : "Doubles"}
                      {event.event_type === "knockout" ? " - Knockout" : event.pace_mode === "continuous" ? " - Continuous" : ""} &middot; {event.num_boards || 4} board{(event.num_boards || 4) !== 1 ? "s" : ""}
                      {event.board_numbers && <span className="text-white/20"> ({event.board_numbers.join(", ")})</span>}
                      {event.effective_boards && event.effective_boards !== (event.num_boards || 4) && (
                        <span className="text-[#309F6A]"> &middot; Using {event.effective_boards} (optimized for {teams.length} teams)</span>
                      )}
                    </p>
                  </div>
                  <Badge className={`rounded-none text-xs tracking-[0.2em] uppercase font-bold ${event.status === "active" ? "bg-[#309F6A] text-white" : "bg-[#E3292E]/20 text-[#E3292E]"}`} data-testid="event-status">
                    {event.status === "teams_generated" ? "TEAMS READY" : event.status.toUpperCase()}
                  </Badge>
                  {event.event_type === "tournament" && (
                    <Badge className="rounded-none text-xs tracking-[0.2em] uppercase font-bold bg-yellow-500/20 text-yellow-400 ml-2" data-testid="event-type-badge">TOURNAMENT</Badge>
                  )}
                </div>

                {/* Player Management */}
                {(event.status === "setup" || event.status === "teams_generated") && event.play_mode !== "teams" && (
                  <Card className="bg-[#141414] border-white/10 rounded-none">
                    <CardHeader>
                      <CardTitle className="font-heading text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <span>Players ({players.length})</span>
                        {players.length >= (event.play_mode === "singles" ? 2 : 4) && (
                          <Button onClick={generateTeams} disabled={loading.generateTeams} className="bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none text-sm font-heading transition-colors duration-200" data-testid="generate-teams-btn">
                            <Shuffle className="mr-1 h-4 w-4" /> {teams.length > 0 ? "RE-GENERATE" : event.play_mode === "singles" ? "GENERATE DRAW" : "GENERATE TEAMS"}
                          </Button>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex gap-2 items-start">
                        <div className="flex-1 relative">
                          <form onSubmit={(e) => { e.preventDefault(); addPlayer(); setSuggestions([]); }} className="flex gap-2">
                            <Input value={newPlayerName} onChange={(e) => handlePlayerNameChange(e.target.value)} onBlur={() => setTimeout(() => setSuggestions([]), 200)} placeholder="Player name" className="bg-transparent border-white/20 text-white rounded-none flex-1 focus-visible:ring-1 focus-visible:ring-[#E3292E]" data-testid="player-name-input" />
                            <Input value={newPlayerMobile} onChange={(e) => setNewPlayerMobile(e.target.value)} placeholder="Mobile" type="tel" className="bg-transparent border-white/20 text-white rounded-none w-32 flex-shrink-0 focus-visible:ring-1 focus-visible:ring-[#E3292E]" data-testid="player-mobile-input" />
                            <Button type="submit" disabled={!newPlayerName.trim() || !newPlayerMobile.trim() || loading.addPlayer} className="bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none transition-colors duration-200" data-testid="add-player-btn">
                              <Plus className="h-4 w-4" />
                            </Button>
                          </form>
                          <p className="text-white/25 text-xs font-body mt-1">Each player needs their own mobile — 1 credit is spent from their wallet.</p>
                          {/* Autocomplete suggestions */}
                          {suggestions.length > 0 && (
                            <div className="absolute z-50 top-full left-0 right-12 mt-1 border border-white/20 bg-[#141414] shadow-lg" data-testid="player-suggestions">
                              {suggestions.map((s) => (
                                <button key={s.id} onClick={() => addSuggestedPlayer(s)} className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/10 flex items-center justify-between border-b border-white/5 transition-colors duration-200" data-testid={`suggestion-${s.name}`}>
                                  <span className="font-body">{s.name}</span>
                                  <span className="text-white/30 text-xs">{s.mobile ? "has credit info" : "needs mobile"}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <Button onClick={openRosterDialog} variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-none text-sm whitespace-nowrap transition-colors duration-200" data-testid="open-roster-btn">
                          <Users className="mr-1 h-4 w-4" /> Database
                        </Button>
                      </div>
                      {players.length < (event.play_mode === "singles" ? 2 : 4) && players.length > 0 && (
                        <p className="text-white/30 text-sm font-body">
                          Need at least {event.play_mode === "singles" ? 2 : 4} players ({(event.play_mode === "singles" ? 2 : 4) - players.length} more needed)
                        </p>
                      )}
                      <div className="space-y-1 max-h-64 overflow-y-auto">
                        {players.map((player, idx) => (
                          <div key={player.id} className="flex items-center justify-between py-2 px-3 border-b border-white/5 hover:bg-white/5 transition-colors duration-200">
                            <span className="text-white text-sm font-body">
                              <span className="text-white/30 mr-3 font-display">{idx + 1}.</span>{player.name}
                            </span>
                            <Button onClick={() => removePlayer(player.id)} variant="ghost" size="sm" className="text-white/30 hover:text-[#E3292E] hover:bg-transparent rounded-none h-8 w-8 p-0" data-testid={`remove-player-${idx}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Team Builder (teams mode only) */}
                {(event.status === "setup" || event.status === "teams_generated") && event.play_mode === "teams" && (
                  <Card className="bg-[#141414] border-white/10 rounded-none" data-testid="team-builder-card">
                    <CardHeader>
                      <CardTitle className="font-heading text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <span>Build Team ({teamBuilderPlayers.length}/{event.team_size || teamSize} players)</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Team Name */}
                      <div>
                        <Label className="text-white/40 text-xs">Team Name</Label>
                        <Input
                          value={teamBuilderName}
                          onChange={(e) => setTeamBuilderName(e.target.value)}
                          placeholder={`Team ${teams.filter(t => !t.removed).length + 1}`}
                          className="bg-transparent border-white/20 text-white rounded-none mt-1 focus-visible:ring-1 focus-visible:ring-[#E3292E]"
                          data-testid="team-builder-name"
                        />
                      </div>

                      {/* Player Slots */}
                      <div className="space-y-2">
                        <Label className="text-white/40 text-xs">Players (each needs a mobile — 1 credit spent per player)</Label>
                        {teamBuilderPlayers.map((p, idx) => (
                          <div key={idx} className="flex items-center gap-2 py-1.5 px-3 border border-white/10 bg-white/[0.02]">
                            <span className="text-white/30 text-xs font-display w-5">{idx + 1}.</span>
                            <span className="text-white text-sm font-body flex-1">{p.name}</span>
                            {p.mobile ? (
                              <span className="text-white/30 text-xs font-body">{p.mobile}</span>
                            ) : (
                              <Input
                                value={p.mobile}
                                onChange={(e) => setTeamBuilderPlayers((prev) => prev.map((pl, i) => i === idx ? { ...pl, mobile: e.target.value } : pl))}
                                placeholder="Mobile"
                                type="tel"
                                className="bg-transparent border-yellow-500/40 text-white rounded-none h-7 w-28 text-xs focus-visible:ring-1 focus-visible:ring-[#E3292E]"
                                data-testid={`tb-mobile-${idx}`}
                              />
                            )}
                            <button onClick={() => removePlayerFromTeamBuilder(idx)} className="text-white/20 hover:text-[#E3292E] transition-colors" data-testid={`tb-remove-${idx}`}>
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                        {teamBuilderPlayers.length < (event.team_size || teamSize) && (
                          <div className="flex gap-2 items-start">
                            <div className="flex-1 relative">
                              <form onSubmit={(e) => { e.preventDefault(); addPlayerToTeamBuilder(teamBuilderInput, teamBuilderMobile); }} className="flex gap-2">
                                <Input
                                  value={teamBuilderInput}
                                  onChange={(e) => {
                                    setTeamBuilderInput(e.target.value);
                                    if (e.target.value.length >= 2 && roster.length > 0) {
                                      const q = e.target.value.toLowerCase();
                                      setTeamBuilderSuggestions(roster.filter(r =>
                                        r.name.toLowerCase().includes(q) &&
                                        !teamBuilderPlayers.some(p => p.name.toLowerCase() === r.name.toLowerCase())
                                      ).slice(0, 5));
                                    } else {
                                      setTeamBuilderSuggestions([]);
                                    }
                                  }}
                                  onBlur={() => setTimeout(() => setTeamBuilderSuggestions([]), 200)}
                                  placeholder={`Player ${teamBuilderPlayers.length + 1}`}
                                  className="bg-transparent border-white/20 text-white rounded-none flex-1 focus-visible:ring-1 focus-visible:ring-[#E3292E]"
                                  data-testid="tb-player-input"
                                />
                                <Input
                                  value={teamBuilderMobile}
                                  onChange={(e) => setTeamBuilderMobile(e.target.value)}
                                  placeholder="Mobile"
                                  type="tel"
                                  className="bg-transparent border-white/20 text-white rounded-none w-28 flex-shrink-0 focus-visible:ring-1 focus-visible:ring-[#E3292E]"
                                  data-testid="tb-player-mobile-input"
                                />
                                <Button type="submit" disabled={!teamBuilderInput.trim()} className="bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none transition-colors duration-200" data-testid="tb-add-player-btn">
                                  <Plus className="h-4 w-4" />
                                </Button>
                              </form>
                              {teamBuilderSuggestions.length > 0 && (
                                <div className="absolute z-50 top-full left-0 right-12 mt-1 border border-white/20 bg-[#141414] shadow-lg">
                                  {teamBuilderSuggestions.map((s) => (
                                    <button key={s.id} onClick={() => { addPlayerToTeamBuilder(s.name, s.mobile || ""); setTeamBuilderSuggestions([]); }} className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/10 flex items-center justify-between border-b border-white/5 transition-colors duration-200">
                                      <span className="font-body">{s.name}</span>
                                      <span className="text-white/30 text-xs">{s.mobile ? s.mobile : "needs mobile"}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            <Button onClick={openRosterForTeamBuilder} variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-none text-sm whitespace-nowrap transition-colors duration-200" data-testid="tb-roster-btn">
                              <Users className="mr-1 h-4 w-4" /> Database
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Save Team / Next Team */}
                      <div className="flex gap-2">
                        <Button
                          onClick={saveTeamFromBuilder}
                          disabled={teamBuilderPlayers.length !== (event.team_size || teamSize) || teamBuilderPlayers.some((p) => !p.mobile)}
                          className="flex-1 bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none font-heading transition-colors duration-200"
                          data-testid="save-team-btn"
                        >
                          <ArrowRight className="mr-1 h-4 w-4" /> NEXT TEAM
                        </Button>
                      </div>
                      {teamBuilderPlayers.length > 0 && teamBuilderPlayers.length < (event.team_size || teamSize) && (
                        <p className="text-white/30 text-sm font-body">
                          Need {(event.team_size || teamSize) - teamBuilderPlayers.length} more player{(event.team_size || teamSize) - teamBuilderPlayers.length !== 1 ? "s" : ""}
                        </p>
                      )}
                      {teamBuilderPlayers.length === (event.team_size || teamSize) && teamBuilderPlayers.some((p) => !p.mobile) && (
                        <p className="text-yellow-400 text-sm font-body">Fill in the missing mobile number(s) above to save this team.</p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Teams Display */}
                {teams.length > 0 && (
                  <Card className="bg-[#141414] border-white/10 rounded-none">
                    <CardHeader>
                      <CardTitle className="font-heading text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <span>Teams ({teams.filter(t => !t.removed).length})</span>
                        {event.status === "teams_generated" && event.event_type !== "knockout" && (
                          <Button onClick={generateSchedule} disabled={loading.generateSchedule} className="bg-[#309F6A] hover:bg-[#28875a] text-white rounded-none text-sm font-heading transition-colors duration-200" data-testid="generate-schedule-btn">
                            <Play className="mr-1 h-4 w-4" /> START MATCHES
                          </Button>
                        )}
                        {event.status === "teams_generated" && event.event_type === "knockout" && teams.filter(t => !t.removed).length >= 4 && (
                          <Button onClick={generateKnockoutBracket} disabled={loading.generateKnockout} className="bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none text-sm font-heading transition-colors duration-200" data-testid="generate-knockout-btn">
                            <Swords className="mr-1 h-4 w-4" /> GENERATE BRACKET
                          </Button>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {reservePlayer && (
                        <div className="mb-4 p-3 border border-yellow-500/20 bg-yellow-500/5">
                          <p className="text-yellow-400 text-sm font-body">Reserve player: <strong>{reservePlayer}</strong> (odd number of players)</p>
                        </div>
                      )}
                      {event.event_type === "knockout" && (() => {
                        const count = teams.filter(t => !t.removed).length;
                        const isPower = count > 0 && (count & (count - 1)) === 0;
                        const nextPow = Math.pow(2, Math.ceil(Math.log2(Math.max(count, 1))));
                        const byes = nextPow - count;
                        if (count >= 4 && !isPower) {
                          return (
                            <div className="mb-4 p-3 border border-yellow-500/20 bg-yellow-500/5" data-testid="bye-warning">
                              <p className="text-yellow-400 text-sm font-body">
                                {count} {event.play_mode === "singles" ? "players" : "teams"} &rarr; {byes} will get a first-round bye.
                                {nextPow <= 32 && <span className="text-white/40"> Add {nextPow - count} more for a clean {nextPow}-bracket.</span>}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {teams.filter(t => !t.removed).map((team) => (
                          <div key={team.id} className="border border-white/10 p-4 hover:border-white/20 transition-colors duration-200 flex items-start justify-between" data-testid={`team-card-${team.id}`}>
                            <div>
                              <p className="font-heading font-bold text-white">{team.team_name}</p>
                              <p className="text-white/50 text-sm mt-1 font-body">
                                {team.players ? team.players.join(", ") : (team.player2 ? `${team.player1} & ${team.player2}` : team.player1 || "")}
                              </p>
                            </div>
                            {event.play_mode === "teams" && (event.status === "setup" || event.status === "teams_generated") && (
                              <button onClick={() => removeSetupTeam(team)} className="text-white/20 hover:text-[#E3292E] transition-colors ml-2 mt-1" data-testid={`remove-setup-team-${team.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* ===== MATCHES TAB ===== */}
          <TabsContent value="matches" className="space-y-6">
            {/* Flow Stepper (not for knockout) */}
            {event && (event.status === "active" || event.status === "completed") && event.event_type !== "knockout" && (
              <TournamentFlowStepper
                event={event}
                tournamentPhase={tournamentState?.tournament_phase || (event.round_robin_complete ? (event.event_type === "tournament" ? "round_robin_done" : "round_robin") : "round_robin")}
              />
            )}

            {(!event || event.status === "setup" || event.status === "teams_generated") && (
              <div className="text-center py-16">
                <Target className="w-12 h-12 text-white/10 mx-auto mb-4" />
                <p className="text-white/30 font-heading">{event?.event_type === "knockout" ? "Generate bracket to start knockout" : "Generate teams and start matches first"}</p>
              </div>
            )}

            {/* ===== KNOCKOUT BRACKET VIEW ===== */}
            {event && event.event_type === "knockout" && (event.status === "active" || event.status === "completed") && knockoutState && (
              <div className="space-y-6">
                {/* Knockout Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-[#E3292E]">
                      KNOCKOUT BRACKET
                    </h2>
                    <p className="text-white/40 text-sm font-body mt-1">
                      {knockoutState.knockout?.status === "complete" ? "Tournament Complete!" : "Click a team to record the winner"}
                    </p>
                  </div>
                  {knockoutState.knockout?.status === "complete" && (
                    <Button onClick={endEvent} variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-none text-sm font-heading" data-testid="end-knockout-btn">
                      END EVENT
                    </Button>
                  )}
                  {event.status === "active" && knockoutState.knockout?.status !== "complete" && (
                    <div className="flex gap-2">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" className="border-[#E3292E]/30 text-[#E3292E] hover:bg-[#E3292E]/10 rounded-none text-sm font-heading" data-testid="end-knockout-early-btn">
                            END EVENT
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[#141414] border-white/10">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-white font-heading">End Knockout Early?</AlertDialogTitle>
                            <AlertDialogDescription className="text-white/60">
                              This will end the tournament before all matches are complete. Results recorded so far will be kept. You can start a new event afterwards.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="border-white/20 text-white hover:bg-white/10 rounded-none">Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={endEvent} className="bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none">End Event</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </div>

                {/* Placements */}
                {knockoutState.knockout?.placements && Object.keys(knockoutState.knockout.placements).length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="knockout-placements">
                    {["1st", "2nd", "3rd", "4th"].map((place) => {
                      const tid = knockoutState.knockout.placements[place];
                      const team = tid ? knockoutState.team_map?.[tid] : null;
                      const colors = { "1st": "#FFD700", "2nd": "#C0C0C0", "3rd": "#CD7F32", "4th": "#666" };
                      return (
                        <div key={place} className="border p-4 text-center" style={{ borderColor: `${colors[place]}40`, backgroundColor: `${colors[place]}08` }} data-testid={`placement-${place}`}>
                          <p className="text-xs tracking-[0.2em] uppercase font-bold mb-2" style={{ color: colors[place] }}>{place} Place</p>
                          {team ? (
                            <>
                              <p className="font-heading font-bold text-white">{team.team_name}</p>
                              <p className="text-white/40 text-xs mt-1">{team.players?.join(", ") || (team.player2 ? `${team.player1} & ${team.player2}` : team.player1)}</p>
                            </>
                          ) : (
                            <p className="text-white/20 text-sm">TBD</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Main Bracket */}
                <div className="border border-white/10 bg-[#141414]/50 p-4">
                  <BracketDiagram
                    matches={(knockoutState.matches || []).filter(m => !m.is_third_place)}
                    teamMap={knockoutState.team_map || {}}
                    championId={knockoutState.knockout?.placements?.["1st"] || null}
                    byeTeamId={null}
                    divisionName="knockout"
                    color="#E3292E"
                    onRecordResult={knockoutState.knockout?.status !== "complete" ? recordBracketResult : undefined}
                    isActive={knockoutState.knockout?.status !== "complete"}
                  />
                </div>

                {/* 3rd Place Match */}
                {(() => {
                  const tpMatch = (knockoutState.matches || []).find(m => m.is_third_place);
                  if (!tpMatch) return null;
                  const tm = knockoutState.team_map || {};
                  const t1 = tm[tpMatch.team1_id] || {};
                  const t2 = tm[tpMatch.team2_id] || {};
                  const t1Won = tpMatch.winner_team_id === tpMatch.team1_id;
                  const t2Won = tpMatch.winner_team_id === tpMatch.team2_id;
                  const isActive = tpMatch.status === "active" && knockoutState.knockout?.status !== "complete";
                  return (
                    <div className="border border-[#CD7F32]/30 bg-[#CD7F32]/5 p-4" data-testid="third-place-match">
                      <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#CD7F32] mb-3">3rd Place Match</p>
                      <div className="flex items-center gap-4">
                        <div
                          className={`flex-1 p-3 border text-center transition-colors ${t1Won ? "border-[#CD7F32] bg-[#CD7F32]/10" : isActive ? "border-white/10 hover:border-white/30 cursor-pointer" : "border-white/10"}`}
                          onClick={() => isActive && !t1Won && !t2Won && recordBracketResult(tpMatch.id, tpMatch.team1_id)}
                        >
                          <p className={`font-heading font-bold ${t1Won ? "text-[#CD7F32]" : "text-white"}`}>{t1.team_name || "TBD"}</p>
                          {t1Won && <Trophy className="w-4 h-4 text-[#CD7F32] mx-auto mt-1" />}
                        </div>
                        <span className="text-white/20 text-xs font-bold">VS</span>
                        <div
                          className={`flex-1 p-3 border text-center transition-colors ${t2Won ? "border-[#CD7F32] bg-[#CD7F32]/10" : isActive ? "border-white/10 hover:border-white/30 cursor-pointer" : "border-white/10"}`}
                          onClick={() => isActive && !t1Won && !t2Won && recordBracketResult(tpMatch.id, tpMatch.team2_id)}
                        >
                          <p className={`font-heading font-bold ${t2Won ? "text-[#CD7F32]" : "text-white"}`}>{t2.team_name || "TBD"}</p>
                          {t2Won && <Trophy className="w-4 h-4 text-[#CD7F32] mx-auto mt-1" />}
                        </div>
                      </div>
                      {tpMatch.status === "completed" && event.status === "active" && (
                        <button onClick={() => openScoreDialog(tpMatch)} className="w-full mt-3 py-1.5 border border-white/10 text-white/40 hover:text-white hover:bg-white/5 text-xs tracking-wider uppercase font-bold transition-colors text-center" data-testid="third-place-score-btn">
                          {tpMatch.high_score || tpMatch.low_score || tpMatch.high_checkout ? "EDIT SCORES" : "ADD SCORES"}
                        </button>
                      )}
                      {(tpMatch.high_score || tpMatch.low_score || tpMatch.high_checkout) && (
                        <div className="mt-2 space-y-0.5">
                          {tpMatch.high_score && <p className="text-[#309F6A] text-[10px] font-body">High: {tpMatch.high_score.player} ({tpMatch.high_score.score})</p>}
                          {tpMatch.high_checkout && <p className="text-yellow-400 text-[10px] font-body">Checkout: {tpMatch.high_checkout.player} ({tpMatch.high_checkout.score})</p>}
                          {tpMatch.low_score && <p className="text-[#E3292E] text-[10px] font-body">Low: {tpMatch.low_score.player} ({tpMatch.low_score.score})</p>}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Knockout Match Scores */}
                {(() => {
                  const completedMatches = (knockoutState.matches || []).filter(m =>
                    m.status === "completed" && !m.is_third_place && m.team1_id && m.team2_id
                  );
                  if (completedMatches.length === 0) return null;
                  const ko = knockoutState.knockout || {};
                  const roundNames = ko.round_names || {};
                  return (
                    <div className="space-y-3" data-testid="knockout-match-scores">
                      <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-white/50">Match Scores</h2>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {completedMatches.map((match) => {
                          const t1Won = match.winner_team_id === match.team1_id;
                          const t2Won = match.winner_team_id === match.team2_id;
                          const roundLabel = roundNames[String(match.bracket_round)] || `Round ${match.bracket_round}`;
                          return (
                            <div key={match.id} className="border border-[#309F6A]/20 bg-[#309F6A]/5 p-4" data-testid={`ko-score-card-${match.id}`}>
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-[10px] tracking-[0.15em] uppercase text-white/30 font-bold">{roundLabel}</p>
                                <CheckCircle className="w-3.5 h-3.5 text-[#309F6A]" />
                              </div>
                              <div className="space-y-1 mb-2">
                                <p className={`text-sm font-heading font-bold ${t1Won ? "text-[#309F6A]" : "text-white/40"}`}>
                                  {match.team1_name} {t1Won && <Trophy className="w-3 h-3 inline ml-1" />}
                                </p>
                                <p className={`text-sm font-heading font-bold ${t2Won ? "text-[#309F6A]" : "text-white/40"}`}>
                                  {match.team2_name} {t2Won && <Trophy className="w-3 h-3 inline ml-1" />}
                                </p>
                              </div>
                              {(match.high_score || match.low_score || match.high_checkout) && (
                                <div className="mb-2 space-y-0.5">
                                  {match.high_score && <p className="text-[#309F6A] text-[10px] font-body">High: {match.high_score.player} ({match.high_score.score})</p>}
                                  {match.high_checkout && <p className="text-yellow-400 text-[10px] font-body">Checkout: {match.high_checkout.player} ({match.high_checkout.score})</p>}
                                  {match.low_score && <p className="text-[#E3292E] text-[10px] font-body">Low: {match.low_score.player} ({match.low_score.score})</p>}
                                </div>
                              )}
                              {event.status === "active" && (
                                <button onClick={() => openScoreDialog(match)} className="w-full py-1.5 border border-white/10 text-white/40 hover:text-white hover:bg-white/5 text-xs tracking-wider uppercase font-bold transition-colors text-center" data-testid={`ko-score-btn-${match.id}`}>
                                  {match.high_score || match.low_score || match.high_checkout ? "EDIT SCORES" : "ADD SCORES"}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Round-by-Round view */}
            {event && (event.status === "active" || event.status === "completed") && event.event_type !== "knockout" && event.pace_mode !== "continuous" && currentRound && !(event.event_type === "tournament" && (event.tournament_phase === "divisions" || event.tournament_phase === "complete" || event.tournament_phase === "round_robin_done")) && (
              <>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-[#E3292E]">
                      Round {currentRound.round_number} {event.total_rounds ? `of ${event.total_rounds}` : ""}
                    </h2>
                    {currentRound.round_robin_complete && (
                      <Badge className="bg-white/10 text-white/60 rounded-none text-xs mt-1">BONUS ROUND</Badge>
                    )}
                  </div>
                  {event.status === "active" && (
                    <div className="flex gap-2">
                      <Button onClick={advanceRound} disabled={loading.advanceRound || !roundComplete} className="bg-[#309F6A] hover:bg-[#28875a] text-white rounded-none text-sm font-heading disabled:opacity-40 transition-colors duration-200" data-testid="advance-round-btn">
                        <ArrowRight className="mr-1 h-4 w-4" /> NEXT ROUND
                      </Button>
                      <Button onClick={endEvent} variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-none text-sm font-heading transition-colors duration-200" data-testid="end-event-btn">
                        END EVENT
                      </Button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
                  {currentRound.matches?.map((match) => (
                    <MatchCard
                      key={match.id}
                      match={match}
                      eventStatus={event.status}
                      onRecordResult={recordResult}
                      onOpenScoreDialog={openScoreDialog}
                    />
                  ))}
                </div>

                {roundComplete && event.status === "active" && (
                  <div className="text-center p-4 border border-[#309F6A]/30 bg-[#309F6A]/5">
                    <p className="text-[#309F6A] font-heading font-bold">All matches complete! Click NEXT ROUND to continue.</p>
                  </div>
                )}
              </>
            )}

            {/* Continuous Play view */}
            {event && event.event_type !== "knockout" && event.pace_mode === "continuous" && (event.status === "active" || event.status === "completed") && boardStatus && !(event.event_type === "tournament" && (event.tournament_phase === "divisions" || event.tournament_phase === "complete" || event.tournament_phase === "round_robin_done")) && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-[#E3292E]">
                      CONTINUOUS PLAY
                    </h2>
                    <p className="text-white/40 text-sm font-body mt-1">
                      {boardStatus.completed_matches} of {boardStatus.total_matches} matches complete
                      {boardStatus.queued_matches > 0 && <span> &middot; {boardStatus.queued_matches} queued</span>}
                    </p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-[#309F6A] transition-all duration-500" style={{ width: `${boardStatus.total_matches > 0 ? (boardStatus.completed_matches / boardStatus.total_matches) * 100 : 0}%` }} />
                    </div>
                    <Button onClick={endEvent} variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-none text-sm font-heading" data-testid="end-event-btn">
                      END EVENT
                    </Button>
                  </div>
                </div>

                {boardStatus.round_robin_complete && event.event_type === "tournament" && (
                  <div className="text-center py-4 border border-yellow-500/20 bg-yellow-500/5">
                    <p className="text-yellow-400 font-heading font-bold">All round-robin matches complete! Generate divisions to start knockout phase.</p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
                  {Array.from({ length: boardStatus.boards }, (_, i) => i + 1).map((boardNum) => {
                    const match = boardStatus.active_matches.find((m) => m.board_number === boardNum);
                    if (!match) return (
                      <div key={boardNum} className="border border-white/5 bg-white/[0.02] p-6" data-testid={`cont-board-${boardNum}-idle`}>
                        <p className="text-xs tracking-[0.2em] uppercase text-white/30 font-bold">Board {boardNum}</p>
                        <p className="text-white/20 text-sm mt-4 font-body">{boardStatus.queued_matches === 0 && boardStatus.completed_matches > 0 ? "All done!" : "Waiting..."}</p>
                      </div>
                    );
                    return (
                      <MatchCard
                        key={boardNum}
                        match={match}
                        eventStatus={event.status}
                        onRecordResult={recordResult}
                        onOpenScoreDialog={openScoreDialog}
                        isContinuous
                        onContinue={continueBoard}
                      />
                    );
                  })}
                </div>

                {boardStatus.round_robin_complete && event.event_type !== "tournament" && (
                  <div className="text-center p-4 border border-[#309F6A]/30 bg-[#309F6A]/5">
                    <p className="text-[#309F6A] font-heading font-bold">All round-robin matches complete!</p>
                  </div>
                )}
              </div>
            )}


            {/* Next Up + Queued Matches */}
            {event && event.status === "active" && (nextUpMatches.length > 0 || upcomingMatches.length > 0) && (
              <div className="mt-6 space-y-6" data-testid="admin-upcoming-section">
                {/* NEXT UP Section */}
                {nextUpMatches.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#309F6A] animate-pulse" />
                      <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-[#309F6A]">
                        Next Up &middot; {availableBoards} board{availableBoards !== 1 ? "s" : ""} available
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      {nextUpMatches.map((match, idx) => (
                        <div key={match.id} className="border border-[#309F6A]/30 bg-[#309F6A]/5 p-4 relative" data-testid={`next-up-match-${idx}`}>
                          <div className="mb-3 flex items-center justify-between">
                            <Badge className="rounded-none text-[9px] bg-[#309F6A]/20 text-[#309F6A] border border-[#309F6A]/30">NEXT UP</Badge>
                            <span className="text-[9px] text-[#309F6A]/60 font-heading">Board {idx + 1}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex-1 text-right">
                              <p className="font-heading font-bold text-white text-sm">{match.team1_name}</p>
                              {match.team1_players && <p className="text-white/40 text-[10px] mt-0.5">{match.team1_players}</p>}
                            </div>
                            <span className="text-[#309F6A]/40 text-[10px] tracking-[0.3em] font-bold flex-shrink-0">VS</span>
                            <div className="flex-1 text-left">
                              <p className="font-heading font-bold text-white text-sm">{match.team2_name}</p>
                              {match.team2_players && <p className="text-white/40 text-[10px] mt-0.5">{match.team2_players}</p>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* REMAINING QUEUE Section */}
                {upcomingMatches.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-white/20" />
                      <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-white/30">
                        Queue &middot; {upcomingMatches.length} more match{upcomingMatches.length !== 1 ? "es" : ""}
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      {upcomingMatches.map((match, idx) => (
                        <div key={match.id} className="border border-dashed border-white/8 bg-white/[0.015] p-4" data-testid={`admin-queued-match-${idx}`}>
                          <div className="mb-3">
                            <Badge className="rounded-none text-[9px] bg-white/5 text-white/20">QUEUED</Badge>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex-1 text-right">
                              <p className="font-heading font-bold text-white/50 text-sm">{match.team1_name}</p>
                              {match.team1_players && <p className="text-white/20 text-[10px] mt-0.5">{match.team1_players}</p>}
                            </div>
                            <span className="text-white/10 text-[10px] tracking-[0.3em] font-bold flex-shrink-0">VS</span>
                            <div className="flex-1 text-left">
                              <p className="font-heading font-bold text-white/50 text-sm">{match.team2_name}</p>
                              {match.team2_players && <p className="text-white/20 text-[10px] mt-0.5">{match.team2_players}</p>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Team Management — Sub / Remove */}
            {event && event.status === "active" && teams.length > 0 && (
              <div className="mt-6" data-testid="team-management-section">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-white/30">Manage Teams</h2>
                  <Button onClick={() => { loadRoster(); setShowAddTeam(true); setAddTeamForm({ player1: "", player2: "", team_name: "" }); }} size="sm" className="bg-[#309F6A] hover:bg-[#28875a] text-white rounded-none text-xs font-heading" data-testid="add-team-mid-btn">
                    <Plus className="mr-1 h-3 w-3" /> {event.play_mode === "doubles" || event.play_mode === "teams" ? "ADD TEAM" : "ADD PLAYER"}
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                  {[...teams].sort((a, b) => {
                    if (a.removed && !b.removed) return 1;
                    if (!a.removed && b.removed) return -1;
                    return b.wins - a.wins || a.losses - b.losses;
                  }).map((team, idx) => (
                    <div key={team.id} className={`border p-3 flex items-center justify-between ${team.removed ? "border-white/5 bg-white/[0.01] opacity-50" : "border-white/8 bg-white/[0.02]"}`} data-testid={`manage-team-card-${idx}`}>
                      <div className="min-w-0 flex-1">
                        <p className={`font-heading font-bold text-sm truncate ${team.removed ? "text-white/40 line-through" : "text-white"}`}>{team.team_name}</p>
                        <p className="text-white/30 text-[10px]">
                          {team.wins}W {team.losses}L
                          {team.removed && <span className="ml-1 text-[#E3292E]/60">(Removed)</span>}
                        </p>
                      </div>
                      {!team.removed && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1.5 text-white/20 hover:text-white hover:bg-white/5 transition-colors flex-shrink-0" data-testid={`team-manage-${idx}`}>
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-[#1a1a1a] border-white/10 rounded-none min-w-[160px]">
                          {team.player1 && (
                            <DropdownMenuItem onClick={() => { setSubDialog({ open: true, team, oldPlayer: team.player1 }); setSubNewPlayer(""); }} className="text-white/70 hover:text-white focus:bg-white/10 focus:text-white rounded-none text-sm" data-testid={`sub-p1-${idx}`}>
                              <ArrowRightLeft className="mr-2 h-3.5 w-3.5" /> Sub {team.player1}
                            </DropdownMenuItem>
                          )}
                          {team.player2 && (
                            <DropdownMenuItem onClick={() => { setSubDialog({ open: true, team, oldPlayer: team.player2 }); setSubNewPlayer(""); }} className="text-white/70 hover:text-white focus:bg-white/10 focus:text-white rounded-none text-sm" data-testid={`sub-p2-${idx}`}>
                              <ArrowRightLeft className="mr-2 h-3.5 w-3.5" /> Sub {team.player2}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => removeTeam(team)} className="text-[#E3292E] hover:text-[#E3292E] focus:bg-[#E3292E]/10 focus:text-[#E3292E] rounded-none text-sm" data-testid={`remove-team-${idx}`}>
                            <Trash2 className="mr-2 h-3.5 w-3.5" /> Remove Team
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}


            {/* Tournament: Generate Divisions prompt */}
            {event && event.event_type === "tournament" && event.tournament_phase === "round_robin_done" && (
              <div className="text-center py-8 border border-yellow-500/20 bg-yellow-500/5">
                <Trophy className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                <p className="text-white font-heading font-bold text-xl mb-2">Round-Robin Complete!</p>
                <p className="text-white/50 font-body mb-6">Teams will be split into Winners and Losers divisions for knockout play</p>
                <Button onClick={generateDivisions} disabled={loading.divisions} className="bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none font-heading font-bold px-8 py-3" data-testid="generate-divisions-btn">
                  <Trophy className="mr-2 h-4 w-4" /> GENERATE DIVISIONS
                </Button>
              </div>
            )}

            {/* Tournament: Bracket Phase with Flow Diagram */}
            {event && event.event_type === "tournament" && tournamentState && (tournamentState.tournament_phase === "divisions" || tournamentState.tournament_phase === "complete") && (
              <div className="space-y-8">
                {["winners", "losers"].map((divKey) => {
                  const div = tournamentState.divisions?.[divKey];
                  if (!div) return null;
                  const tm = tournamentState.team_map || {};
                  const divColor = divKey === "winners" ? "#309F6A" : "#E3292E";
                  const divMatches = (tournamentState.bracket_matches || []).filter(
                    (m) => m.phase === `${divKey}_bracket`
                  );

                  return (
                    <div key={divKey} data-testid={`division-${divKey}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <h2 className="text-xs tracking-[0.2em] uppercase font-bold" style={{ color: divColor }}>
                          {div.name}
                        </h2>
                        <span className="text-white/20 text-xs font-body">
                          {div.team_ids?.length} teams
                        </span>
                      </div>

                      <div className="border border-white/10 bg-[#141414] p-4">
                        <BracketDiagram
                          matches={divMatches}
                          teamMap={tm}
                          championId={div.champion_id}
                          byeTeamId={div.bye_team_id}
                          divisionName={divKey}
                          color={divColor}
                          onRecordResult={event.status === "active" ? recordBracketResult : undefined}
                          isActive={event.status === "active"}
                        />
                      </div>

                      {/* Interactive match cards for current bracket round */}
                      {!div.champion_id && event.status === "active" && (() => {
                        const currentBR = tournamentState.bracket_round || 1;
                        const activeMatches = divMatches.filter((m) => m.bracket_round === currentBR);
                        if (activeMatches.length === 0) return null;
                        return (
                          <div className="mt-4">
                            <p className="text-xs tracking-[0.15em] uppercase text-white/40 font-bold mb-3">Record Results - Round {currentBR}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                              {activeMatches.map((match) => {
                                const isComplete = match.status === "completed";
                                const team1Won = match.winner_team_id === match.team1_id;
                                const team2Won = match.winner_team_id === match.team2_id;
                                return (
                                  <div key={match.id} className={`border p-5 transition-colors duration-200 ${isComplete ? "border-[#309F6A]/30 bg-[#309F6A]/5" : "border-white/10 bg-[#0A0A0A]"}`} data-testid={`bracket-card-${match.id}`}>
                                    <div className="flex items-center justify-between mb-4">
                                      <p className="text-xs tracking-[0.2em] uppercase text-white/50 font-bold">Board {match.board_number}</p>
                                      {isComplete && <CheckCircle className="w-4 h-4 text-[#309F6A]" />}
                                    </div>
                                    <div className="space-y-3">
                                      <div className={`p-3 border transition-colors ${team1Won ? "border-[#309F6A] bg-[#309F6A]/10" : "border-white/10"}`}>
                                        <p className={`font-heading font-bold ${team1Won ? "text-[#309F6A]" : "text-white"}`}>{match.team1_name}</p>
                                        {match.team1_players && match.team1_players.includes("&") && <p className="text-white/40 text-xs mt-1">{match.team1_players}</p>}
                                        {!team1Won && (
                                          <Button onClick={() => recordBracketResult(match.id, match.team1_id)} size="sm" className={`mt-2 rounded-none text-xs ${isComplete ? "bg-white/10 text-white/60 hover:bg-white/20" : "bg-[#309F6A] hover:bg-[#28875a] text-white"}`} data-testid={`bracket-win1-${match.id}`}>
                                            <Trophy className="mr-1 h-3 w-3" /> WIN
                                          </Button>
                                        )}
                                        {team1Won && <div className="flex items-center gap-1 mt-2"><Trophy className="w-4 h-4 text-[#309F6A]" /><span className="text-[#309F6A] text-xs font-bold">WINNER</span></div>}
                                      </div>
                                      <p className="text-center text-white/20 text-xs tracking-[0.3em] uppercase font-bold">VS</p>
                                      <div className={`p-3 border transition-colors ${team2Won ? "border-[#309F6A] bg-[#309F6A]/10" : "border-white/10"}`}>
                                        <p className={`font-heading font-bold ${team2Won ? "text-[#309F6A]" : "text-white"}`}>{match.team2_name}</p>
                                        {match.team2_players && match.team2_players.includes("&") && <p className="text-white/40 text-xs mt-1">{match.team2_players}</p>}
                                        {!team2Won && (
                                          <Button onClick={() => recordBracketResult(match.id, match.team2_id)} size="sm" className={`mt-2 rounded-none text-xs ${isComplete ? "bg-white/10 text-white/60 hover:bg-white/20" : "bg-[#309F6A] hover:bg-[#28875a] text-white"}`} data-testid={`bracket-win2-${match.id}`}>
                                            <Trophy className="mr-1 h-3 w-3" /> WIN
                                          </Button>
                                        )}
                                        {team2Won && <div className="flex items-center gap-1 mt-2"><Trophy className="w-4 h-4 text-[#309F6A]" /><span className="text-[#309F6A] text-xs font-bold">WINNER</span></div>}
                                      </div>
                                    </div>
                                    {isComplete && (
                                      <button onClick={() => openScoreDialog(match)} className="w-full mt-3 py-1.5 border border-white/10 text-white/40 hover:text-white hover:bg-white/5 text-xs tracking-wider uppercase font-bold transition-colors text-center" data-testid={`bracket-score-${match.id}`}>
                                        {match.high_score || match.low_score || match.high_checkout ? "EDIT SCORES" : "ADD SCORES"}
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}

                {/* Advance bracket button */}
                {event.status === "active" && tournamentState.tournament_phase === "divisions" && (() => {
                  const br = tournamentState.bracket_round;
                  const currentMatches = (tournamentState.bracket_matches || []).filter((m) => m.bracket_round === br);
                  const allDone = currentMatches.length > 0 && currentMatches.every((m) => m.status === "completed");
                  return allDone ? (
                    <div className="flex gap-2">
                      <Button onClick={advanceBracket} disabled={loading.advanceBracket} className="bg-[#309F6A] hover:bg-[#28875a] text-white rounded-none font-heading font-bold" data-testid="advance-bracket-btn">
                        <ArrowRight className="mr-1 h-4 w-4" /> NEXT BRACKET ROUND
                      </Button>
                      <Button onClick={endEvent} variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-none font-heading" data-testid="end-tournament-btn">
                        END TOURNAMENT
                      </Button>
                    </div>
                  ) : null;
                })()}

                {tournamentState.tournament_phase === "complete" && (
                  <div className="text-center p-8 border border-yellow-500/30 bg-yellow-500/5 space-y-6">
                    <div>
                      <Trophy className="w-12 h-12 text-yellow-400 mx-auto mb-3" />
                      <p className="font-display text-2xl text-yellow-400 uppercase">Tournament Complete!</p>
                      <p className="text-white/50 font-body text-sm mt-2">All division champions have been crowned</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      {event.status === "active" && (
                        <Button onClick={endEvent} className="bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none font-heading font-bold px-8" data-testid="end-tournament-final-btn">
                          <CheckCircle className="mr-2 h-4 w-4" /> END TOURNAMENT
                        </Button>
                      )}
                      {event.status === "completed" && (
                        <>
                          <Button onClick={rematchEvent} disabled={loading.rematch} className="bg-[#309F6A] hover:bg-[#28875a] text-white rounded-none font-heading font-bold px-8" data-testid="rematch-from-bracket-btn">
                            <RotateCcw className="mr-2 h-4 w-4" /> REMATCH
                          </Button>
                          <Button onClick={() => { setEvent(null); setPlayers([]); setTeams([]); setCurrentRound(null); setReservePlayer(null); setTournamentState(null); setBoardStatus(null); setUpcomingMatches([]); setNumBoards(4); }} variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-none font-heading font-bold px-8" data-testid="new-game-from-bracket-btn">
                            <Plus className="mr-2 h-4 w-4" /> NEW EVENT
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* ===== STATS TAB ===== */}
          <TabsContent value="stats" className="space-y-6">
            {!event && careerStats.length === 0 && (
              <div className="text-center py-16">
                <Target className="w-12 h-12 text-white/10 mx-auto mb-4" />
                <p className="text-white/30 font-heading">No event data</p>
              </div>
            )}

            {event && teams.length > 0 && (
              <>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-[#E3292E]">Standings - {event.name}</h2>
                  <Button onClick={downloadPDF} className="bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none text-sm font-heading transition-colors duration-200" data-testid="download-pdf-btn">
                    <Download className="mr-1 h-4 w-4" /> DOWNLOAD PDF
                  </Button>
                </div>

                <div className="border border-white/10 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b border-white/10 hover:bg-transparent">
                        <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase">#</TableHead>
                        <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase">Team</TableHead>
                        <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase hidden sm:table-cell">Players</TableHead>
                        <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase text-center">P</TableHead>
                        <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase text-center">W</TableHead>
                        <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase text-center">L</TableHead>
                        <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase text-center">%</TableHead>
                        {event.status === "active" && <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase w-10"></TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[...teams].sort((a, b) => b.wins - a.wins || a.losses - b.losses).map((team, idx) => {
                        const pct = team.games_played > 0 ? Math.round((team.wins / team.games_played) * 100) : 0;
                        return (
                          <TableRow key={team.id} className={`border-b border-white/5 ${team.removed ? "opacity-50" : "hover:bg-white/5"}`} data-testid={`admin-standings-row-${idx}`}>
                            <TableCell className="font-display text-xl text-white/30">{idx + 1}</TableCell>
                            <TableCell>
                              <span className={`font-heading font-bold ${team.removed ? "text-white/40 line-through" : "text-white"}`}>{team.team_name}</span>
                              {team.removed && <span className="ml-2 text-[9px] text-[#E3292E]/60 uppercase tracking-wider">(Removed)</span>}
                            </TableCell>
                            <TableCell className="text-white/40 text-sm hidden sm:table-cell font-body">
                              {team.players ? team.players.join(", ") : (team.player2 ? `${team.player1} & ${team.player2}` : team.player1)}
                            </TableCell>
                            <TableCell className="text-center text-white/60">{team.games_played}</TableCell>
                            <TableCell className="text-center text-[#309F6A] font-bold">{team.wins}</TableCell>
                            <TableCell className="text-center text-[#E3292E]">{team.losses}</TableCell>
                            <TableCell className="text-center text-white font-bold">{pct}%</TableCell>
                            {event.status === "active" && (
                              <TableCell className="p-1">
                                {!team.removed && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button className="p-1.5 text-white/20 hover:text-white hover:bg-white/5 transition-colors" data-testid={`team-manage-${idx}`}>
                                      <MoreVertical className="w-4 h-4" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent className="bg-[#1a1a1a] border-white/10 rounded-none min-w-[160px]">
                                    {team.player1 && (
                                      <DropdownMenuItem onClick={() => { setSubDialog({ open: true, team, oldPlayer: team.player1 }); setSubNewPlayer(""); }} className="text-white/70 hover:text-white focus:bg-white/10 focus:text-white rounded-none text-sm" data-testid={`sub-player1-${idx}`}>
                                        <ArrowRightLeft className="mr-2 h-3.5 w-3.5" /> Sub {team.player1}
                                      </DropdownMenuItem>
                                    )}
                                    {team.player2 && (
                                      <DropdownMenuItem onClick={() => { setSubDialog({ open: true, team, oldPlayer: team.player2 }); setSubNewPlayer(""); }} className="text-white/70 hover:text-white focus:bg-white/10 focus:text-white rounded-none text-sm" data-testid={`sub-player2-${idx}`}>
                                        <ArrowRightLeft className="mr-2 h-3.5 w-3.5" /> Sub {team.player2}
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem onClick={() => removeTeam(team)} className="text-[#E3292E] hover:text-[#E3292E] focus:bg-[#E3292E]/10 focus:text-[#E3292E] rounded-none text-sm" data-testid={`remove-team-${idx}`}>
                                      <Trash2 className="mr-2 h-3.5 w-3.5" /> Remove Team
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}

            {/* Notable Scores for current event */}
            {event && currentRound && (() => {
              const allMatches = currentRound?.matches || [];
              // We need to scan all matches, let's use a simpler approach - show from loaded matches
              const hsMatches = allMatches.filter((m) => m.high_score);
              const lsMatches = allMatches.filter((m) => m.low_score);
              return (hsMatches.length > 0 || lsMatches.length > 0) ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {hsMatches.length > 0 && (
                    <div className="border border-[#309F6A]/20 bg-[#309F6A]/5 p-4">
                      <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#309F6A] mb-3">High Scores</h3>
                      {hsMatches.map((m) => (
                        <div key={m.id} className="flex justify-between py-1 border-b border-white/5">
                          <span className="text-white text-sm font-body">{m.high_score.player}</span>
                          <span className="text-[#309F6A] font-heading font-bold">{m.high_score.score}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {lsMatches.length > 0 && (
                    <div className="border border-[#E3292E]/20 bg-[#E3292E]/5 p-4">
                      <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#E3292E] mb-3">Low Scores</h3>
                      {lsMatches.map((m) => (
                        <div key={m.id} className="flex justify-between py-1 border-b border-white/5">
                          <span className="text-white text-sm font-body">{m.low_score.player}</span>
                          <span className="text-[#E3292E] font-heading font-bold">{m.low_score.score}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : null;
            })()}

            {/* Career Statistics - scoped to current event's players */}
            {careerStats.length > 0 && (
                  <div className="mt-8">
                    <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-[#309F6A] mb-4 flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" /> {event ? `Player Career Stats (${event.name})` : "Career Statistics (All Events)"}
                    </h2>
                    <div className="border border-white/10 overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-b border-white/10 hover:bg-transparent">
                            <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase">#</TableHead>
                            <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase">Player</TableHead>
                            <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase text-center">Events</TableHead>
                            <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase text-center">Games</TableHead>
                            <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase text-center">W</TableHead>
                            <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase text-center">L</TableHead>
                            <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase text-center">%</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(() => {
                            const eventPlayerNames = event ? new Set(teams.flatMap((t) => [t.player1, t.player2].filter(Boolean)).map((n) => n.toLowerCase())) : null;
                            const filtered = eventPlayerNames ? careerStats.filter((p) => eventPlayerNames.has(p.name.toLowerCase())) : careerStats;
                            return filtered.map((player, idx) => {
                            const pct = player.games > 0 ? Math.round((player.wins / player.games) * 100) : 0;
                            return (
                              <TableRow key={player.name} className="border-b border-white/5 hover:bg-white/5" data-testid={`career-row-${idx}`}>
                                <TableCell className="font-display text-xl text-white/30">{idx + 1}</TableCell>
                                <TableCell><span className="font-heading font-bold text-white">{player.name}</span></TableCell>
                                <TableCell className="text-center text-white/60">{player.events}</TableCell>
                                <TableCell className="text-center text-white/60">{player.games}</TableCell>
                                <TableCell className="text-center text-[#309F6A] font-bold">{player.wins}</TableCell>
                                <TableCell className="text-center text-[#E3292E]">{player.losses}</TableCell>
                                <TableCell className="text-center text-white font-bold">{pct}%</TableCell>
                              </TableRow>
                            );
                          });
                          })()}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
          </TabsContent>
          {/* ===== HISTORY TAB ===== */}
          <TabsContent value="history" className="space-y-6">
            <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-[#E3292E]">Event History (Last 30 Days)</h2>
            {pastEvents.length === 0 && (
              <div className="text-center py-16">
                <History className="w-12 h-12 text-white/10 mx-auto mb-4" />
                <p className="text-white/30 font-heading">No past events</p>
              </div>
            )}
            <div className="space-y-3">
              {pastEvents.map((evt) => (
                <div key={evt.id} className={`border p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-colors duration-200 ${historyStats?.event?.id === evt.id ? "border-[#E3292E]/30 bg-[#E3292E]/5" : "border-white/10 bg-[#141414] hover:border-white/20"}`} data-testid={`history-event-${evt.id}`}>
                  <div>
                    <p className="font-heading font-bold text-white">{evt.name}</p>
                    <p className="text-white/50 text-sm font-body">{evt.date}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`rounded-none text-xs ${evt.status === "completed" ? "bg-white/10 text-white/60" : "bg-[#309F6A] text-white"}`}>
                      {evt.status?.toUpperCase()}
                    </Badge>
                    <Button onClick={() => loadHistoryStats(evt.id)} size="sm" variant="outline" className="rounded-none border-white/20 text-white hover:bg-white/10 text-xs" data-testid={`view-stats-${evt.id}`}>
                      <Eye className="mr-1 h-3 w-3" /> Stats
                    </Button>
                    <Button onClick={() => downloadEventPDF(evt.id, evt.name)} size="sm" className="bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none text-xs" data-testid={`download-pdf-${evt.id}`}>
                      <Download className="mr-1 h-3 w-3" /> PDF
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="border-[#E3292E]/30 text-[#E3292E] hover:bg-[#E3292E]/10 rounded-none text-xs" data-testid={`delete-event-${evt.id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#141414] border-white/10">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white font-heading">Delete "{evt.name}"?</AlertDialogTitle>
                          <AlertDialogDescription className="text-white/60">
                            This will permanently delete this event and all its matches, teams, and stats. This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="border-white/20 text-white hover:bg-white/10 rounded-none">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteEvent(evt.id, evt.name)} className="bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>

            {historyStats && (
              <Card className="bg-[#141414] border-white/10 rounded-none mt-6">
                <CardHeader>
                  <CardTitle className="font-heading text-white flex items-center justify-between">
                    <span>{historyStats.event?.name} - Stats</span>
                    <Button onClick={() => setHistoryStats(null)} variant="ghost" size="sm" className="text-white/30 hover:text-white rounded-none">Close</Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-white/50 text-sm mb-4 font-body">
                    {historyStats.completed_matches} of {historyStats.total_matches} matches completed
                  </p>
                  <div className="border border-white/10 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-white/10 hover:bg-transparent">
                          <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase">#</TableHead>
                          <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase">Team</TableHead>
                          <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase text-center">W</TableHead>
                          <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase text-center">L</TableHead>
                          <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase text-center">%</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {historyStats.teams?.map((t, idx) => {
                          const pct = t.games_played > 0 ? Math.round((t.wins / t.games_played) * 100) : 0;
                          return (
                            <TableRow key={t.id} className="border-b border-white/5 hover:bg-white/5">
                              <TableCell className="font-display text-lg text-white/30">{idx + 1}</TableCell>
                              <TableCell>
                                <span className="font-heading font-bold text-white">{t.team_name}</span>
                                <span className="text-white/30 text-xs ml-2">{t.player1} & {t.player2}</span>
                              </TableCell>
                              <TableCell className="text-center text-[#309F6A] font-bold">{t.wins}</TableCell>
                              <TableCell className="text-center text-[#E3292E]">{t.losses}</TableCell>
                              <TableCell className="text-center text-white font-bold">{pct}%</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ===== MANAGE TAB (Super Admin) ===== */}
          {role === "super_admin" && (
            <TabsContent value="manage" className="space-y-6">
              {/* Pending Approvals */}
              <div>
                <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-[#E3292E] mb-4 flex items-center gap-2">
                  <UserCheck className="h-4 w-4" /> Pending Approvals ({pendingAdmins.length})
                </h2>
                {pendingAdmins.length === 0 ? (
                  <div className="border border-white/10 bg-[#141414] p-6 text-center">
                    <p className="text-white/30 font-body">No pending registrations</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingAdmins.map((adm) => (
                      <div key={adm.id} className="border border-yellow-500/20 bg-yellow-500/5 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`pending-admin-${adm.id}`}>
                        <div>
                          <p className="font-heading font-bold text-white">{adm.username}</p>
                          <p className="text-white/40 text-xs font-body">Registered: {adm.created_at?.split("T")[0]}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={() => approveAdmin(adm.id)} size="sm" className="bg-[#309F6A] hover:bg-[#28875a] text-white rounded-none text-xs" data-testid={`approve-admin-${adm.id}`}>
                            <UserCheck className="mr-1 h-3 w-3" /> APPROVE
                          </Button>
                          <Button onClick={() => rejectAdmin(adm.id)} size="sm" variant="outline" className="border-[#E3292E]/30 text-[#E3292E] hover:bg-[#E3292E]/10 rounded-none text-xs" data-testid={`reject-admin-${adm.id}`}>
                            <UserX className="mr-1 h-3 w-3" /> REJECT
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Venues */}
              <div>
                <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-[#E3292E] mb-4 flex items-center gap-2">
                  <Target className="h-4 w-4" /> Venues ({allVenues.length})
                </h2>
                {allVenues.length === 0 ? (
                  <div className="border border-white/10 bg-[#141414] p-6 text-center">
                    <p className="text-white/30 font-body">No venues registered</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {allVenues.map((v) => (
                      <div key={v.id} className="border border-white/10 bg-[#141414] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`venue-${v.id}`}>
                        <div>
                          <p className="font-heading font-bold text-white">{v.name}</p>
                          <p className="text-white/40 text-xs font-body">
                            {v.admin_count} admin{v.admin_count !== 1 ? "s" : ""} &middot; {v.event_count} event{v.event_count !== 1 ? "s" : ""} &middot; Created {v.created_at?.split("T")[0]}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Button onClick={() => deleteVenue(v)} size="sm" variant="outline" className="rounded-none border-[#E3292E]/30 text-[#E3292E]/70 hover:bg-[#E3292E]/20 hover:text-[#E3292E] hover:border-[#E3292E]/50 text-xs h-7 px-2" data-testid={`delete-venue-${v.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* All Admins */}
              <div>
                <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-[#E3292E] mb-4 flex items-center gap-2">
                  <Shield className="h-4 w-4" /> All Admins ({allAdmins.length})
                </h2>
                <div className="border border-white/10 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b border-white/10 hover:bg-transparent">
                        <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase">Username</TableHead>
                        <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase">Role</TableHead>
                        <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase">Status</TableHead>
                        <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase hidden sm:table-cell">Registered</TableHead>
                        <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allAdmins.map((adm) => (
                        <TableRow key={adm.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`admin-row-${adm.id}`}>
                          <TableCell className="font-heading font-bold text-white">{adm.username}</TableCell>
                          <TableCell>
                            <Badge className={`rounded-none text-xs ${adm.role === "super_admin" ? "bg-[#E3292E] text-white" : "bg-white/10 text-white/60"}`}>
                              {adm.role === "super_admin" ? "APP MANAGER" : "ADMIN"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={`rounded-none text-xs ${adm.status === "approved" ? "bg-[#309F6A]/20 text-[#309F6A]" : adm.status === "pending" ? "bg-yellow-500/20 text-yellow-400" : "bg-white/10 text-white/40"}`}>
                              {adm.status?.toUpperCase()}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-white/40 text-sm hidden sm:table-cell font-body">{adm.created_at?.split("T")[0]}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              {adm.role !== "super_admin" && adm.status !== "pending" && (
                                <Button onClick={() => toggleAdminStatus(adm.id, adm.status)} size="sm" variant="outline" className="rounded-none border-white/20 text-white/60 hover:bg-white/10 text-xs" data-testid={`toggle-admin-${adm.id}`}>
                                  {adm.status === "approved" ? "Deactivate" : "Activate"}
                                </Button>
                              )}
                              {adm.role !== "super_admin" && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button size="sm" variant="ghost" className="rounded-none text-white/30 hover:text-yellow-400 hover:bg-transparent h-7 w-7 p-0" title="Reset Password" data-testid={`reset-pw-${adm.id}`}>
                                      <KeyRound className="h-3.5 w-3.5" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent className="bg-[#141414] border-white/10 rounded-none">
                                    <AlertDialogHeader>
                                      <AlertDialogTitle className="font-heading text-white">Reset password for {adm.username}?</AlertDialogTitle>
                                      <AlertDialogDescription className="text-white/50 font-body">
                                        This will generate a new random password for <span className="text-white font-bold">{adm.username}</span>. You'll need to share the new password with them.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel className="rounded-none border-white/20 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => forceResetPassword(adm.id)} className="rounded-none bg-yellow-500 hover:bg-yellow-600 text-black font-heading font-bold" data-testid={`confirm-reset-pw-${adm.id}`}>RESET PASSWORD</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                              {adm.role !== "super_admin" && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button size="sm" variant="ghost" className="rounded-none text-white/30 hover:text-[#E3292E] hover:bg-transparent h-7 w-7 p-0" data-testid={`delete-admin-${adm.id}`}>
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent className="bg-[#141414] border-white/10 rounded-none">
                                    <AlertDialogHeader>
                                      <AlertDialogTitle className="font-heading text-white">Delete {adm.username}?</AlertDialogTitle>
                                      <AlertDialogDescription className="text-white/50 font-body">
                                        This will permanently delete the admin account <span className="text-white font-bold">{adm.username}</span>. This cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel className="rounded-none border-white/20 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={async () => { try { await axios.delete(`${API}/admin/${adm.id}`, authConfig()); toast.success(`${adm.username} deleted`); loadAllAdmins(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } }} className="rounded-none bg-[#E3292E] hover:bg-[#DC2626] text-white font-heading">DELETE</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Reset Password Result Dialog */}
              <Dialog open={!!resetPwResult} onOpenChange={(open) => { if (!open) setResetPwResult(null); }}>
                <DialogContent className="bg-[#141414] border-white/10 rounded-none max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-white flex items-center gap-2">
                      <KeyRound className="h-5 w-5 text-yellow-400" /> Password Reset
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <p className="text-white/60 text-sm font-body">
                      New password generated for <span className="text-white font-bold">{resetPwResult?.username}</span>. Copy it now — it won't be shown again.
                    </p>
                    <div className="flex items-center gap-2 bg-black/50 border border-white/10 p-3">
                      <code className="text-yellow-400 font-mono text-lg flex-1 select-all" data-testid="reset-pw-value">{resetPwResult?.new_password}</code>
                      <Button size="sm" variant="ghost" className="text-white/50 hover:text-white h-8 w-8 p-0" data-testid="copy-reset-pw" onClick={() => { navigator.clipboard.writeText(resetPwResult?.new_password || ""); toast.success("Password copied!"); }}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <Button onClick={() => setResetPwResult(null)} className="w-full bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none font-heading" data-testid="close-reset-pw">DONE</Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Dart Boards */}
              <div>
                <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-[#E3292E] mb-4 flex items-center gap-2">
                  <Target className="h-4 w-4" /> Dart Boards ({venueBoards.length})
                </h2>
                <div className="border border-white/10 bg-[#141414] p-4 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-white/50 text-sm font-body">Each board has a permanent QR code. Print and place at each physical dartboard.</p>
                    <Button onClick={() => {
                      const svgEls = venueBoards.map(b => {
                        const el = document.querySelector(`[data-testid="board-qr-${b.board_number}"] .qr-wrap svg`);
                        return { board_number: b.board_number, svg: el ? new XMLSerializer().serializeToString(el) : "", url: `${window.location.origin}/board/${b.id}` };
                      });
                      const w = window.open("", "_blank");
                      w.document.write(`<html><head><title>Board QR Codes - ${venueName}</title><style>body{font-family:Arial,sans-serif;background:#fff;padding:20px;} .grid{display:grid;grid-template-columns:repeat(2,1fr);gap:24px;} .board{text-align:center;border:2px solid #000;padding:20px;page-break-inside:avoid;} .board h2{margin:0 0 8px;font-size:24px;} .board svg{width:200px;height:200px;} .board p{margin:4px 0;font-size:12px;color:#666;} @media print{.no-print{display:none;}}</style></head><body>`);
                      w.document.write(`<h1 style="text-align:center;margin-bottom:24px;">${venueName} - Board QR Codes</h1>`);
                      w.document.write(`<p style="text-align:center;color:#666;margin-bottom:24px;" class="no-print">Print this page and cut out each QR code to place at your dart boards.</p>`);
                      w.document.write(`<div class="grid">`);
                      svgEls.forEach(b => {
                        w.document.write(`<div class="board"><h2>Board ${b.board_number}</h2>${b.svg}<p>${b.url}</p></div>`);
                      });
                      w.document.write(`</div><script>setTimeout(function(){window.print();},300);<\/script></body></html>`);
                      w.document.close();
                    }} variant="outline" className="border-white/20 text-white/60 hover:bg-white/10 rounded-none font-heading text-xs" data-testid="print-boards-btn">
                      <Printer className="h-3.5 w-3.5 mr-1.5" /> PRINT ALL
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                    {venueBoards.map(b => (
                      <div key={b.id} className="border border-white/10 p-3 text-center relative group" data-testid={`board-qr-${b.board_number}`}>
                        <button onClick={() => {
                          const svgEl = document.querySelector(`[data-testid="board-qr-${b.board_number}"] .qr-wrap svg`);
                          const svgHtml = svgEl ? new XMLSerializer().serializeToString(svgEl) : "";
                          const w = window.open("", "_blank");
                          w.document.write(`<html><head><title>Board ${b.board_number} - ${venueName}</title><style>body{font-family:Arial,sans-serif;background:#fff;display:flex;justify-content:center;align-items:center;min-height:100vh;margin:0;} .card{text-align:center;border:3px solid #000;padding:40px 60px;} .card h1{margin:0 0 12px;font-size:36px;} .card h2{margin:0 0 20px;font-size:18px;color:#666;} .card svg{width:280px;height:280px;} .card p{margin:12px 0 0;font-size:11px;color:#999;}</style></head><body><div class="card"><h1>Board ${b.board_number}</h1><h2>${venueName}</h2>${svgHtml}<p>Scan to submit match result</p></div><script>setTimeout(function(){window.print();},300);<\/script></body></html>`);
                          w.document.close();
                        }} className="absolute top-1.5 right-1.5 text-white/20 hover:text-white/60 transition-colors" title="Print this board" data-testid={`print-board-${b.board_number}`}>
                          <Printer className="h-3.5 w-3.5" />
                        </button>
                        <p className="text-white font-heading font-bold text-sm mb-2">Board {b.board_number}</p>
                        <div className="bg-white p-2 inline-block mx-auto qr-wrap">
                          <QRCode value={`${window.location.origin}/board/${b.id}`} size={80} />
                        </div>
                        <p className="text-white/20 text-[9px] font-body mt-1.5 break-all">/board/{b.id.slice(0, 8)}...</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Season Management */}
              <div>
                <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-[#E3292E] mb-4 flex items-center gap-2">
                  <RotateCcw className="h-4 w-4" /> Season Management
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="border border-white/10 bg-[#141414] p-5">
                    <h3 className="font-heading font-bold text-white mb-2">New Season</h3>
                    <p className="text-white/40 text-xs font-body mb-4">Clears all events, matches, teams, and game history. Player roster is preserved for re-use next season.</p>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" className="w-full rounded-none border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 font-heading" data-testid="clear-season-btn">
                          <RotateCcw className="mr-2 h-4 w-4" /> START NEW SEASON
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#141414] border-white/10 rounded-none">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="font-heading text-white flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-yellow-400" /> Clear Season Data?
                          </AlertDialogTitle>
                          <AlertDialogDescription className="text-white/50 font-body">
                            This will permanently delete all events, matches, teams, and game history. Your <span className="text-white font-bold">player roster will be kept</span> for the next season. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-none border-white/20 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={clearSeason} disabled={loading.clearSeason} className="rounded-none bg-yellow-500 hover:bg-yellow-600 text-black font-heading font-bold" data-testid="confirm-clear-season">
                            YES, CLEAR SEASON
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>

                  <div className="border border-[#E3292E]/20 bg-[#141414] p-5">
                    <h3 className="font-heading font-bold text-white mb-2">Factory Reset</h3>
                    <p className="text-white/40 text-xs font-body mb-4">Clears absolutely everything: events, matches, teams, game history, AND the player roster. Only admin accounts are kept.</p>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" className="w-full rounded-none border-[#E3292E]/30 text-[#E3292E] hover:bg-[#E3292E]/10 font-heading" data-testid="factory-reset-btn">
                          <TrashIcon className="mr-2 h-4 w-4" /> FACTORY RESET
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#141414] border-white/10 rounded-none">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="font-heading text-white flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-[#E3292E]" /> Factory Reset?
                          </AlertDialogTitle>
                          <AlertDialogDescription className="text-white/50 font-body">
                            This will permanently delete <span className="text-[#E3292E] font-bold">ALL data</span> including events, matches, teams, game history, AND the entire player roster. Only admin accounts will remain. This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-none border-white/20 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={factoryReset} disabled={loading.factoryReset} className="rounded-none bg-[#E3292E] hover:bg-[#DC2626] text-white font-heading font-bold" data-testid="confirm-factory-reset">
                            YES, DELETE EVERYTHING
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>

              {/* Player Roster Management */}
              <div>
                <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-[#E3292E] mb-4 flex items-center gap-2">
                  <Users className="h-4 w-4" /> Player Roster ({adminRoster.length} players)
                </h2>
                <Input
                  value={adminRosterSearch} onChange={(e) => setAdminRosterSearch(e.target.value)}
                  placeholder="Search players..." className="bg-transparent border-white/20 text-white rounded-none mb-3 focus-visible:ring-1 focus-visible:ring-[#E3292E]"
                  data-testid="admin-roster-search"
                />
                <div className="border border-white/10 max-h-80 overflow-y-auto">
                  {adminRoster
                    .filter((p) => !adminRosterSearch || p.name.toLowerCase().includes(adminRosterSearch.toLowerCase()))
                    .map((player) => (
                      <div key={player.id} className="flex items-center justify-between px-3 py-2 border-b border-white/5 hover:bg-white/5 transition-colors duration-200" data-testid={`admin-roster-player-${player.id}`}>
                        <div>
                          <span className="text-white text-sm font-body">{player.name}</span>
                          <span className="text-white/30 text-xs ml-2">{player.events_count} event{player.events_count !== 1 ? "s" : ""}</span>
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-white/30 hover:text-[#E3292E] hover:bg-transparent rounded-none h-7 w-7 p-0" data-testid={`delete-roster-${player.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#141414] border-white/10 rounded-none">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="font-heading text-white">Remove {player.name}?</AlertDialogTitle>
                              <AlertDialogDescription className="text-white/50 font-body">
                                This will remove <span className="text-white font-bold">{player.name}</span> from the player roster database. They won't appear in quick-add suggestions for future events.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-none border-white/20 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteRosterPlayer(player.id, player.name)} className="rounded-none bg-[#E3292E] hover:bg-[#DC2626] text-white font-heading">
                                REMOVE
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    ))}
                  {adminRoster.length === 0 && (
                    <div className="p-6 text-center">
                      <p className="text-white/30 text-sm font-body">No players in roster</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          )}

        </Tabs>

        <QRDialog open={showQR} onOpenChange={setShowQR} venueId={venueId} eventId={event?.id} />
        <GrantCreditsDialog open={showGrantCredits} onOpenChange={setShowGrantCredits} authConfig={authConfig} />
        <RosterDialog
          open={showRoster} onOpenChange={(v) => { setShowRoster(v); if (!v) setRosterForTeamBuilder(false); }}
          roster={roster} players={rosterForTeamBuilder ? teamBuilderPlayers : players}
          rosterSearch={rosterSearch} setRosterSearch={setRosterSearch}
          selectedRoster={selectedRoster} setSelectedRoster={setSelectedRoster}
          onTogglePlayer={toggleRosterPlayer} onAddSelected={addSelectedFromRoster}
          loading={loading.addPlayer}
        />
        <ScoreDialog
          open={scoreDialog.open} match={scoreDialog.match}
          scoreForm={scoreForm} setScoreForm={setScoreForm}
          onClose={() => setScoreDialog({ open: false, match: null })}
          onSubmit={submitScores}
        />

        {/* Match Result Confirmation Dialog (Social/Tournament) */}
        <Dialog open={!!matchConfirm} onOpenChange={(v) => { if (!v) { setMatchConfirm(null); setMatchConfirmWinner(null); }}}>
          <DialogContent className="bg-[#141414] border-white/10 rounded-none max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-white text-xl">Confirm Match Result</DialogTitle>
            </DialogHeader>
            {matchConfirm && (() => {
              const winnerName = matchConfirmWinner === matchConfirm.team1_id ? matchConfirm.team1_name : matchConfirm.team2_name;
              const loserName = matchConfirmWinner === matchConfirm.team1_id ? matchConfirm.team2_name : matchConfirm.team1_name;
              const parsePs = (s) => {
                if (!s || !s.trim()) return [];
                if (s.includes(" & ")) return s.split(" & ").map(n => n.trim()).filter(Boolean);
                if (s.includes(", ")) return s.split(", ").map(n => n.trim()).filter(Boolean);
                return [s.trim()];
              };
              const allPlayers = [...parsePs(matchConfirm.team1_players), ...parsePs(matchConfirm.team2_players)];
              if (allPlayers.length === 0) {
                if (matchConfirm.team1_name) allPlayers.push(matchConfirm.team1_name);
                if (matchConfirm.team2_name) allPlayers.push(matchConfirm.team2_name);
              }
              return (
                <div className="space-y-5">
                  <div className="text-center text-white/30 text-xs mb-1">Board {matchConfirm.board_number}</div>
                  <div className="border border-[#309F6A]/30 bg-[#309F6A]/5 p-4 text-center">
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Winner</p>
                    <p className="text-[#309F6A] font-heading font-bold text-xl">{winnerName}</p>
                    <p className="text-white/30 text-xs mt-1">defeats {loserName}</p>
                  </div>
                  <div className="space-y-3">
                    <p className="text-white/50 text-xs tracking-[0.15em] uppercase font-bold">Match Scores (optional)</p>
                    {[
                      { key: "hs", label: "High Score", color: "#309F6A", testId: "match-confirm-hs" },
                      { key: "ls", label: "Low Score", color: "#E3292E", testId: "match-confirm-ls" },
                      { key: "hc", label: "Highest Checkout", color: "#EAB308", testId: "match-confirm-hc" },
                    ].map(({ key, label, color, testId }) => (
                      <div key={key} className="flex gap-2">
                        <select id={`match-${key}-player`} className="flex-1 bg-transparent border text-white text-sm p-2 focus:outline-none" style={{ borderColor: `${color}40` }} data-testid={`${testId}-player`}>
                          <option value="" className="bg-[#141414]">{label} — Player</option>
                          {allPlayers.map((p) => <option key={p} value={p} className="bg-[#141414]">{p}</option>)}
                        </select>
                        <input type="number" id={`match-${key}-score`} placeholder="Score" className="w-24 bg-transparent border text-white text-sm p-2 focus:outline-none" style={{ borderColor: `${color}40` }} data-testid={`${testId}-score`} />
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button onClick={() => { setMatchConfirm(null); setMatchConfirmWinner(null); }} variant="outline" className="flex-1 border-white/20 text-white hover:bg-white/10 rounded-none font-heading" data-testid="match-confirm-cancel">Cancel</Button>
                    <Button onClick={() => {
                      const getVal = (key) => {
                        const p = document.getElementById(`match-${key}-player`)?.value || "";
                        const s = parseInt(document.getElementById(`match-${key}-score`)?.value) || 0;
                        return p && s ? { player: p, score: s } : null;
                      };
                      submitMatchResult(matchConfirm.id, matchConfirmWinner, { high_score: getVal("hs"), low_score: getVal("ls"), high_checkout: getVal("hc") });
                    }} className="flex-1 bg-[#309F6A] hover:bg-[#28875a] text-white rounded-none font-heading font-bold" data-testid="match-confirm-submit">Confirm Result</Button>
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Knockout Win Confirmation Dialog */}
        <Dialog open={!!koConfirmMatch} onOpenChange={(v) => { if (!v) { setKoConfirmMatch(null); setKoConfirmWinner(null); }}}>
          <DialogContent className="bg-[#141414] border-white/10 rounded-none max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-white text-xl">Confirm Match Result</DialogTitle>
            </DialogHeader>
            {koConfirmMatch && (() => {
              const tm = knockoutState?.team_map || {};
              const t1 = tm[koConfirmMatch.team1_id] || {};
              const t2 = tm[koConfirmMatch.team2_id] || {};
              const winnerName = koConfirmWinner === koConfirmMatch.team1_id ? (t1.team_name || koConfirmMatch.team1_name) : (t2.team_name || koConfirmMatch.team2_name);
              const loserName = koConfirmWinner === koConfirmMatch.team1_id ? (t2.team_name || koConfirmMatch.team2_name) : (t1.team_name || koConfirmMatch.team1_name);
              // Build player list for dropdowns
              const parsePs = (s) => {
                if (!s || !s.trim()) return [];
                if (s.includes(" & ")) return s.split(" & ").map(n => n.trim()).filter(Boolean);
                if (s.includes(", ")) return s.split(", ").map(n => n.trim()).filter(Boolean);
                return [s.trim()];
              };
              const allPlayers = [...parsePs(koConfirmMatch.team1_players), ...parsePs(koConfirmMatch.team2_players)];
              if (allPlayers.length === 0) {
                if (koConfirmMatch.team1_name) allPlayers.push(koConfirmMatch.team1_name);
                if (koConfirmMatch.team2_name) allPlayers.push(koConfirmMatch.team2_name);
              }
              return (
                <div className="space-y-5">
                  <div className="border border-[#309F6A]/30 bg-[#309F6A]/5 p-4 text-center">
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Winner</p>
                    <p className="text-[#309F6A] font-heading font-bold text-xl">{winnerName}</p>
                    <p className="text-white/30 text-xs mt-1">defeats {loserName}</p>
                  </div>
                  <div className="space-y-3">
                    <p className="text-white/50 text-xs tracking-[0.15em] uppercase font-bold">Match Scores (optional)</p>
                    {[
                      { key: "hs", label: "High Score", color: "#309F6A", testId: "ko-confirm-hs" },
                      { key: "ls", label: "Low Score", color: "#E3292E", testId: "ko-confirm-ls" },
                      { key: "hc", label: "Highest Checkout", color: "#EAB308", testId: "ko-confirm-hc" },
                    ].map(({ key, label, color, testId }) => (
                      <div key={key} className="flex gap-2">
                        <select
                          id={`ko-${key}-player`}
                          className="flex-1 bg-transparent border text-white text-sm p-2 focus:outline-none" style={{ borderColor: `${color}40` }}
                          data-testid={`${testId}-player`}
                        >
                          <option value="" className="bg-[#141414]">{label} — Player</option>
                          {allPlayers.map((p) => <option key={p} value={p} className="bg-[#141414]">{p}</option>)}
                        </select>
                        <input
                          type="number" id={`ko-${key}-score`} placeholder="Score"
                          className="w-24 bg-transparent border text-white text-sm p-2 focus:outline-none" style={{ borderColor: `${color}40` }}
                          data-testid={`${testId}-score`}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button onClick={() => { setKoConfirmMatch(null); setKoConfirmWinner(null); }} variant="outline" className="flex-1 border-white/20 text-white hover:bg-white/10 rounded-none font-heading" data-testid="ko-confirm-cancel">
                      Cancel
                    </Button>
                    <Button onClick={() => {
                      const getVal = (key) => {
                        const p = document.getElementById(`ko-${key}-player`)?.value || "";
                        const s = parseInt(document.getElementById(`ko-${key}-score`)?.value) || 0;
                        return p && s ? { player: p, score: s } : null;
                      };
                      const scores = { high_score: getVal("hs"), low_score: getVal("ls"), high_checkout: getVal("hc") };
                      submitBracketResult(koConfirmMatch.id, koConfirmWinner, scores);
                    }} className="flex-1 bg-[#309F6A] hover:bg-[#28875a] text-white rounded-none font-heading font-bold" data-testid="ko-confirm-submit">
                      Confirm Result
                    </Button>
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
        <InviteDialog open={showInvite} onOpenChange={setShowInvite} venueName={venueName} authConfig={authConfig} />
        <SetupGuideDialog open={showGuide} onOpenChange={setShowGuide} />

        {/* Add Team Mid-Event Dialog */}
        <Dialog open={showAddTeam} onOpenChange={(v) => { if (!v) setAddTeamForm({ player1: "", player1_mobile: "", player2: "", player2_mobile: "", team_name: "", teamPlayers: [], teamMobiles: [] }); setShowAddTeam(v); }}>
          <DialogContent className="bg-[#141414] border-white/10 rounded-none max-w-sm max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-white text-xl flex items-center gap-2">
                <Plus className="h-5 w-5 text-[#309F6A]" /> {event?.play_mode === "teams" ? "Add New Team" : event?.play_mode === "doubles" ? "Add New Team" : "Add New Player"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-white/50 text-sm font-body">
                {event?.play_mode === "teams"
                  ? `Add ${event?.team_size || 2} players to create a new team. New matches will be queued.`
                  : event?.play_mode === "doubles"
                    ? "Add 2 players to create a new team. New matches will be queued against all existing teams."
                    : "Add a player. New matches will be queued against all existing players."}
              </p>
              <div className="space-y-3">
                <div>
                  <Label className="text-white/40 text-xs uppercase tracking-wider mb-1.5 block">Team Name</Label>
                  <Input value={addTeamForm.team_name} onChange={(e) => setAddTeamForm((p) => ({ ...p, team_name: e.target.value }))} placeholder={`e.g. Team ${teams.length + 1}`} className="bg-transparent border-white/20 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#309F6A]" data-testid="add-team-name-input" />
                  <p className="text-white/30 text-[10px] mt-1">Leave blank to auto-generate</p>
                </div>
                {event?.play_mode === "teams" ? (
                  <>
                    {Array.from({ length: event?.team_size || 2 }, (_, i) => (
                      <div key={i} className="flex gap-2">
                        <div className="flex-1">
                          <Label className="text-white/40 text-xs uppercase tracking-wider mb-1.5 block">Player {i + 1}</Label>
                          <Input
                            value={addTeamForm.teamPlayers[i] || ""}
                            onChange={(e) => {
                              const updated = [...(addTeamForm.teamPlayers || [])];
                              updated[i] = e.target.value;
                              setAddTeamForm((p) => ({ ...p, teamPlayers: updated }));
                            }}
                            placeholder={`Player ${i + 1}`}
                            className="bg-transparent border-white/20 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#309F6A]"
                            data-testid={`add-team-player-${i}`}
                          />
                        </div>
                        <div className="w-28 flex-shrink-0">
                          <Label className="text-white/40 text-xs uppercase tracking-wider mb-1.5 block">Mobile</Label>
                          <Input
                            value={addTeamForm.teamMobiles[i] || ""}
                            onChange={(e) => {
                              const updated = [...(addTeamForm.teamMobiles || [])];
                              updated[i] = e.target.value;
                              setAddTeamForm((p) => ({ ...p, teamMobiles: updated }));
                            }}
                            placeholder="Mobile"
                            type="tel"
                            className="bg-transparent border-white/20 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#309F6A]"
                            data-testid={`add-team-player-mobile-${i}`}
                          />
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Label className="text-white/40 text-xs uppercase tracking-wider mb-1.5 block">Player 1</Label>
                        <Input value={addTeamForm.player1} onChange={(e) => setAddTeamForm((p) => ({ ...p, player1: e.target.value }))} placeholder="Type name or pick below" className="bg-transparent border-white/20 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#309F6A]" data-testid="add-team-p1-input" />
                      </div>
                      <div className="w-28 flex-shrink-0">
                        <Label className="text-white/40 text-xs uppercase tracking-wider mb-1.5 block">Mobile</Label>
                        <Input value={addTeamForm.player1_mobile} onChange={(e) => setAddTeamForm((p) => ({ ...p, player1_mobile: e.target.value }))} placeholder="Mobile" type="tel" className="bg-transparent border-white/20 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#309F6A]" data-testid="add-team-p1-mobile-input" />
                      </div>
                    </div>
                    {roster.length > 0 && (
                      <div className="border border-white/10 overflow-y-auto -mt-2" style={{ maxHeight: "100px" }}>
                        {roster.filter((r) => {
                          const s = addTeamForm.player1.toLowerCase();
                          return !s || r.name.toLowerCase().includes(s);
                        }).slice(0, 20).map((r) => (
                          <button key={r.id} onClick={() => setAddTeamForm((p) => ({ ...p, player1: r.name, player1_mobile: r.mobile || p.player1_mobile }))} className={`w-full text-left px-3 py-1.5 text-sm border-b border-white/5 transition-colors flex items-center justify-between ${addTeamForm.player1 === r.name ? "bg-[#309F6A]/10 text-[#309F6A]" : "text-white/60 hover:bg-white/5"}`}>
                            <span>{r.name}</span>
                            <span className="text-white/30 text-xs">{r.mobile || "needs mobile"}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {event?.play_mode === "doubles" && (
                      <>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <Label className="text-white/40 text-xs uppercase tracking-wider mb-1.5 block">Player 2</Label>
                            <Input value={addTeamForm.player2} onChange={(e) => setAddTeamForm((p) => ({ ...p, player2: e.target.value }))} placeholder="Type name or pick below" className="bg-transparent border-white/20 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#309F6A]" data-testid="add-team-p2-input" />
                          </div>
                          <div className="w-28 flex-shrink-0">
                            <Label className="text-white/40 text-xs uppercase tracking-wider mb-1.5 block">Mobile</Label>
                            <Input value={addTeamForm.player2_mobile} onChange={(e) => setAddTeamForm((p) => ({ ...p, player2_mobile: e.target.value }))} placeholder="Mobile" type="tel" className="bg-transparent border-white/20 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#309F6A]" data-testid="add-team-p2-mobile-input" />
                          </div>
                        </div>
                        {roster.length > 0 && (
                          <div className="border border-white/10 overflow-y-auto -mt-2" style={{ maxHeight: "100px" }}>
                            {roster.filter((r) => {
                              const s = addTeamForm.player2.toLowerCase();
                              const isP1 = r.name.toLowerCase() === addTeamForm.player1.toLowerCase();
                              return !isP1 && (!s || r.name.toLowerCase().includes(s));
                            }).slice(0, 20).map((r) => (
                              <button key={r.id} onClick={() => setAddTeamForm((p) => ({ ...p, player2: r.name, player2_mobile: r.mobile || p.player2_mobile }))} className={`w-full text-left px-3 py-1.5 text-sm border-b border-white/5 transition-colors flex items-center justify-between ${addTeamForm.player2 === r.name ? "bg-[#309F6A]/10 text-[#309F6A]" : "text-white/60 hover:bg-white/5"}`}>
                                <span>{r.name}</span>
                                <span className="text-white/30 text-xs">{r.mobile || "needs mobile"}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
              <Button
                onClick={addTeamMidEvent}
                disabled={event?.play_mode === "teams"
                  ? ((addTeamForm.teamPlayers || []).filter(p => p?.trim()).length !== (event?.team_size || 2)
                      || addTeamForm.teamPlayers.some((p, i) => p?.trim() && !(addTeamForm.teamMobiles[i] || "").trim()))
                  : (!addTeamForm.player1.trim() || !addTeamForm.player1_mobile.trim()
                      || (event?.play_mode === "doubles" && (!addTeamForm.player2.trim() || !addTeamForm.player2_mobile.trim())))}
                className="w-full bg-[#309F6A] hover:bg-[#28875a] text-white rounded-none font-heading font-bold" data-testid="add-team-confirm-btn"
              >
                <Plus className="mr-2 h-4 w-4" /> {event?.play_mode === "singles" ? "ADD PLAYER" : "ADD TEAM"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={subDialog.open} onOpenChange={(v) => { if (!v) { setSubDialog({ open: false, team: null, oldPlayer: "" }); setSubNewPlayer(""); } }}>
          <DialogContent className="bg-[#141414] border-white/10 rounded-none max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-heading text-white text-xl flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5 text-[#E3292E]" /> Substitute Player
              </DialogTitle>
            </DialogHeader>
            {subDialog.team && (
              <div className="space-y-4">
                <p className="text-white/50 text-sm font-body">
                  Replace <span className="text-white font-bold">{subDialog.oldPlayer}</span> in <span className="text-white font-bold">{subDialog.team.team_name}</span>
                </p>
                <Input value={subNewPlayer} onChange={(e) => setSubNewPlayer(e.target.value)} placeholder="Type new player name or search below" className="bg-transparent border-white/20 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#E3292E]" data-testid="sub-new-player-input" />
                {roster.length > 0 && (
                  <div className="border border-white/10 overflow-y-auto" style={{ maxHeight: "180px" }}>
                    {roster
                      .filter((r) => {
                        const name = r.name.toLowerCase();
                        const search = subNewPlayer.toLowerCase();
                        const isOld = name === subDialog.oldPlayer.toLowerCase();
                        const inTeam = subDialog.team.player1?.toLowerCase() === name || subDialog.team.player2?.toLowerCase() === name;
                        return !isOld && !inTeam && (!search || name.includes(search));
                      })
                      .map((r) => (
                        <button
                          key={r.id}
                          onClick={() => setSubNewPlayer(r.name)}
                          className={`w-full text-left px-3 py-2 flex items-center justify-between border-b border-white/5 transition-colors ${subNewPlayer === r.name ? "bg-[#E3292E]/10 border-l-2 border-l-[#E3292E]" : "hover:bg-white/5"}`}
                          data-testid={`sub-roster-${r.name}`}
                        >
                          <span className="text-white text-sm font-body">{r.name}</span>
                          <span className="text-white/25 text-xs">{r.events_count} event{r.events_count !== 1 ? "s" : ""}</span>
                        </button>
                      ))}
                  </div>
                )}
                <Button onClick={substitutePlayer} disabled={!subNewPlayer.trim()} className="w-full bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none font-heading font-bold" data-testid="sub-confirm-btn">
                  <ArrowRightLeft className="mr-2 h-4 w-4" /> SUBSTITUTE
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
